/** This file must only contain pure code and pure imports */

/* eslint-disable @typescript-eslint/naming-convention */
import { serialize, expandToProperty, addAccessorsForMaterialProperty } from "../../Misc/decorators";
import { GetEnvironmentFuzzBRDFTexture, GetOpenPBREnvironmentBRDFTexture } from "../../Misc/brdfTextureTools";
import { type Nullable } from "../../types";
import { type Scene } from "../../scene.pure";
import { Color3, Color4 } from "../../Maths/math.color.pure";
import { ImageProcessingConfiguration, RegisterImageProcessingConfiguration } from "../imageProcessingConfiguration.pure";
import { type BaseTexture } from "../../Materials/Textures/baseTexture.pure";
import { type ThinTexture } from "../../Materials/Textures/thinTexture";
import { Texture } from "../Textures/texture.pure";
import { Material } from "../material.pure";
import { SerializationHelper } from "../../Misc/decorators.serialization";
import { type Engine } from "../../Engines/engine.pure";
import { type AbstractMesh } from "../../Meshes/abstractMesh.pure";
import { type Effect, type IEffectCreationOptions } from "../../Materials/effect.pure";
import { MaterialDefines } from "../materialDefines";
import { ImageProcessingDefinesMixin } from "../imageProcessingConfiguration.defines";
import { EffectFallbacks } from "../effectFallbacks";
import { AddClipPlaneUniforms, BindClipPlane } from "../clipPlaneMaterialHelper";
import { PrepareVertexPullingUniforms, BindVertexPullingUniforms, type IVertexPullingMetadata } from "../vertexPullingHelper.functions";
import {
    BindBonesParameters,
    BindFogParameters,
    BindLights,
    BindLogDepth,
    BindMorphTargetParameters,
    BindTextureMatrix,
    BindIBLParameters,
    BindIBLSamplers,
    HandleFallbacksForShadows,
    PrepareAttributesForBakedVertexAnimation,
    PrepareAttributesForBones,
    PrepareAttributesForInstances,
    PrepareAttributesForMorphTargets,
    PrepareDefinesForAttributes,
    PrepareDefinesForFrameBoundValues,
    PrepareDefinesForLights,
    PrepareDefinesForIBL,
    PrepareDefinesForMergedUV,
    PrepareDefinesForMisc,
    PrepareDefinesForMultiview,
    PrepareDefinesForOIT,
    PrepareDefinesForPrePass,
    PrepareUniformsAndSamplersList,
    PrepareUniformsAndSamplersForIBL,
    PrepareUniformLayoutForIBL,
    AreLightsTexturesReady,
} from "../materialHelper.functions";
import { Constants } from "../../Engines/constants";
import { VertexBuffer } from "../../Buffers/buffer.pure";
import { MaterialPluginEvent } from "../materialPluginEvent";
import { MaterialHelperGeometryRendering } from "../materialHelper.geometryrendering";
import { PrePassConfiguration } from "../prePassConfiguration";
import { type IMaterialCompilationOptions, type ICustomShaderNameResolveOptions } from "../../Materials/material.pure";
import { ShaderLanguage } from "../shaderLanguage";
import { MaterialFlags } from "../materialFlags";
import { type SubMesh } from "../../Meshes/subMesh.pure";
import { Logger } from "core/Misc/logger";
import { UVDefinesMixin } from "../uv.defines";
import { PrepassDefinesMixin } from "../prepass.defines";
import { EnvironmentLightingDefinesMixin } from "../environmentLighting.defines";
import { Vector2, Vector3, Vector4, TmpVectors, type Matrix } from "core/Maths/math.vector.pure";
import { type Mesh } from "../../Meshes/mesh.pure";
import { ImageProcessingMixin } from "../imageProcessing";
import { PushMaterial } from "../pushMaterial";
import { SmartArray } from "../../Misc/smartArray";
import { type RenderTargetTexture } from "../Textures/renderTargetTexture.pure";
import { type IAnimatable } from "../../Animations/animatable.interface";
import { Tools } from "../../Misc/tools.pure";
import { type UniformBuffer } from "../../Materials/uniformBuffer";
import { GeometryBufferRenderer } from "core/Rendering/geometryBufferRenderer.pure";
import { RegisterClass } from "../../Misc/typeStore";

const onCreatedEffectParameters = { effect: null as unknown as Effect, subMesh: null as unknown as Nullable<SubMesh> };

function _GetComponentCount(value: PropertyType): number {
    if (typeof value === "number") {
        return 1;
    }
    if (value instanceof Vector2) {
        return 2;
    }
    if (value instanceof Vector3 || value instanceof Color3) {
        return 3;
    }
    if (value instanceof Vector4 || value instanceof Color4) {
        return 4;
    }

    throw new Error("Unsupported OpenPBR property type.");
}

class Uniform {
    public name: string;
    public numComponents: number;
    public linkedProperties: { [name: string]: Property<PropertyType> } = {};
    /**
     * Cached key of the first entry of `linkedProperties`, set when the first
     * property is linked. Used by the per-frame bind loop to avoid an
     * `Object.keys(linkedProperties)[0]` allocation when reading scalar
     * uniforms.
     */
    public firstLinkedKey: string = "";
    /**
     * Optional define name. If set, the per-frame bind loop will skip pushing
     * this uniform to the UBO unless `defines[requiredDefine]` is true. The
     * UBO slot still exists in the layout; only the per-frame update is
     * skipped, which is safe because the shader only reads these uniforms
     * inside the same #ifdef block.
     */
    public requiredDefine?: string;
    public populateVectorFromLinkedProperties(vector: Vector4 | Vector3 | Vector2): void {
        const destinationSize = _GetComponentCount(vector);
        for (const propKey in this.linkedProperties) {
            const prop = this.linkedProperties[propKey];
            const sourceSize = prop.numComponents;
            if (destinationSize < sourceSize || prop.targetUniformComponentOffset > destinationSize - sourceSize) {
                if (sourceSize == 1) {
                    Logger.Error(`Float property ${prop.name} has an offset that is too large.`);
                } else {
                    Logger.Error(`Vector${sourceSize} property ${prop.name} won't fit in Vector${destinationSize} or has an offset that is too large.`);
                }
                return;
            }
            if (typeof prop.value === "number") {
                Uniform._tmpArray[prop.targetUniformComponentOffset] = prop.value;
            } else {
                prop.value.toArray(Uniform._tmpArray, prop.targetUniformComponentOffset);
            }
        }
        vector.fromArray(Uniform._tmpArray);
    }
    public constructor(name: string, componentNum: number) {
        this.name = name;
        this.numComponents = componentNum;
    }
    private static _tmpArray: number[] = [0, 0, 0, 0];
}

type PropertyType = Vector2 | Vector3 | Vector4 | number | Color3 | Color4;

/**
 * Defines a property for the OpenPBRMaterial.
 */
class Property<T extends PropertyType> {
    public name: string;
    public targetUniformName: string;
    public defaultValue: T;
    public value: T;
    // public includeAlphaFromProp: string = "";

    /**
     * If not given a type, there will be no uniform defined for this property and
     * it will be assumed that the value will be packed into the already existing "uniformName" uniform.
     */
    public targetUniformComponentNum: number = 4; // Default to vec4
    public targetUniformComponentOffset: number = 0;
    /**
     * Optional define name. If set, the per-frame bind loop will skip pushing
     * the owning uniform to the UBO unless this define is active. All
     * properties packed into the same uniform must share the same
     * `requiredDefine` (or none of them must set it) for the gating to apply.
     */
    public requiredDefine?: string;

    /**
     * Creates a new Property instance.
     * @param name The name of the property in the shader
     * @param defaultValue The default value of the property
     * @param targetUniformName The name of the property in the shader uniform block
     * @param targetUniformComponentNum The number of components in the target uniform. All properties that are
     * packed into the same uniform must agree on the size of the target uniform.
     * @param targetUniformComponentOffset The offset in the uniform where this property will be packed.
     * @param requiredDefine Optional define name. When provided, the per-frame
     *  bind loop will skip pushing the owning uniform to the UBO unless
     *  `defines[requiredDefine]` is true.
     */
    constructor(name: string, defaultValue: T, targetUniformName: string, targetUniformComponentNum: number, targetUniformComponentOffset: number = 0, requiredDefine?: string) {
        this.name = name;
        this.targetUniformName = targetUniformName;
        this.defaultValue = defaultValue;
        this.value = defaultValue;
        this.targetUniformComponentNum = targetUniformComponentNum;
        this.targetUniformComponentOffset = targetUniformComponentOffset;
        this.requiredDefine = requiredDefine;
    }

    /**
     * Returns the number of components of the property based on its default value type.
     */
    public get numComponents(): number {
        if (typeof this.defaultValue === "number") {
            return 1;
        }
        return _GetComponentCount(this.defaultValue);
    }
}

class Sampler {
    public name: string;
    public value: Nullable<BaseTexture> = null; // Texture value, default to null
    public samplerPrefix: string = ""; // The name of the sampler in the shader
    public textureDefine: string = ""; // The define used in the shader for this sampler

    /**
     * The name of the sampler used in the shader.
     * If this naming changes, we'll also need to change:
     * - samplerFragmentDeclaration.fx
     * - openpbr.fragment.fx
     */
    public get samplerName(): string {
        return this.samplerPrefix + "Sampler";
    }
    /**
     * The name of the sampler info used in the shader.
     * If this naming changes, we'll also need to change:
     * - openpbr.vertex.fx
     * - openpbr.fragment.fx
     */
    public get samplerInfoName(): string {
        return "v" + this.samplerPrefix.charAt(0).toUpperCase() + this.samplerPrefix.slice(1) + "Infos";
    }
    /**
     * The name of the matrix used for this sampler in the shader.
     * If this naming changes, we'll also need to change:
     * - materialHelper.functions.BindTextureMatrix
     * - samplerVertexImplementation.fx
     * - openpbr.fragment.fx
     */
    public get samplerMatrixName(): string {
        return this.samplerPrefix + "Matrix";
    }
    /**
     * Creates a new Sampler instance.
     * @param name The name of the texture property
     * @param samplerPrefix The prefix used for the name of the sampler in the shader
     * @param textureDefine The define used in the shader for this sampler
     */
    constructor(name: string, samplerPrefix: string, textureDefine: string) {
        this.name = name;
        this.samplerPrefix = samplerPrefix;
        this.textureDefine = textureDefine;
    }
}

class OpenPBRMaterialDefinesBase extends PrepassDefinesMixin(UVDefinesMixin(MaterialDefines)) {}

class OpenPBRMaterialDefinesWithEnvLighting extends EnvironmentLightingDefinesMixin(OpenPBRMaterialDefinesBase) {}

/**
 * Manages the defines for the PBR Material.
 * @internal
 */
export class OpenPBRMaterialDefines extends ImageProcessingDefinesMixin(OpenPBRMaterialDefinesWithEnvLighting) {
    public NUM_SAMPLES = "0";
    public REALTIME_FILTERING = false;
    public IBL_CDF_FILTERING = false;
    public LIGHTCOUNT = 0;
    public VERTEXCOLOR = false;

    public BAKED_VERTEX_ANIMATION_TEXTURE = false;

    public VERTEXALPHA = false;
    public ALPHATEST = false;
    public DEPTHPREPASS = false;
    public ALPHABLEND = false;
    public ALPHA_FROM_BASE_COLOR_TEXTURE = false;
    public ALPHATESTVALUE = "0.5";
    public PREMULTIPLYALPHA = false;

    public REFLECTIVITY_GAMMA = false;
    public REFLECTIVITYDIRECTUV = 0;
    public SPECULARTERM = false;

    public LODBASEDMICROSFURACE = true;

    public SPECULAR_ROUGHNESS_FROM_METALNESS_TEXTURE_GREEN = false;
    public BASE_METALNESS_FROM_METALNESS_TEXTURE_BLUE = false;
    public AOSTOREINMETALMAPRED = false;
    public SPECULAR_WEIGHT_IN_ALPHA = false;
    public SPECULAR_WEIGHT_FROM_SPECULAR_COLOR_TEXTURE = false;
    public SPECULAR_ROUGHNESS_ANISOTROPY_FROM_TANGENT_TEXTURE = false;
    public COAT_ROUGHNESS_FROM_GREEN_CHANNEL = false;
    public COAT_ROUGHNESS_ANISOTROPY_FROM_TANGENT_TEXTURE = false;
    public USE_GLTF_STYLE_ANISOTROPY = false;
    public THIN_FILM_THICKNESS_FROM_THIN_FILM_TEXTURE = false;
    public FUZZ_ROUGHNESS_FROM_TEXTURE_ALPHA = false;
    public SUBSURFACE_WEIGHT_FROM_TEXTURE_ALPHA = false;
    public GEOMETRY_THICKNESS_FROM_GREEN_CHANNEL = false;

    public ENVIRONMENTBRDF = false;
    public ENVIRONMENTBRDF_RGBD = false;
    public FUZZENVIRONMENTBRDF = false;

    public NORMAL = false;
    public TANGENT = false;
    public OBJECTSPACE_NORMALMAP = false;
    public PARALLAX = false;
    public PARALLAX_RHS = false;
    public PARALLAXOCCLUSION = false;
    public NORMALXYSCALE = true;
    /**
     * Enables anisotropic logic. Still needed because it's used in pbrHelperFunctions
     */
    public ANISOTROPIC = false;
    /**
     * Tells the shader to use OpenPBR's anisotropic roughness remapping
     */
    public ANISOTROPIC_OPENPBR = true;
    /**
     * Tells the shader to apply anisotropy to the base layer
     */
    public ANISOTROPIC_BASE = false;
    /**
     * Tells the shader to apply anisotropy to the coat layer
     */
    public ANISOTROPIC_COAT = false;

    /**
     * Number of samples to use for the fuzz IBL lighting calculations
     */
    public FUZZ_IBL_SAMPLES = 6;

    /**
     * Enables the 4-tap rotated-grid kernel for refractive background blur.
     * When false, a single dithered sample is used instead.
     */
    public REFRACTION_HIGH_QUALITY_BLUR = false;

    /**
     * Tells the shader to enable the fuzz layer
     */
    public FUZZ = false;

    /**
     * Tells the shader to enable the thin film layer
     */
    public THIN_FILM = false;

    /**
     * Tells the shader to enable the legacy iridescence code
     * Iridescence is the name of thin film interference in the PBR material.
     */
    public IRIDESCENCE = false;

    /**
     * Tells the shader to enable dispersion in refraction
     */
    public DISPERSION = false;

    /**
     * Enables subsurface scattering
     */
    public SCATTERING = false;

    /**
     * Enables the use of screen-space irradiance texture for scattering
     */
    public USE_IRRADIANCE_TEXTURE_FOR_SCATTERING = false;

    /**
     * Number of samples used by the screen-space SSS convolution kernel.
     */
    public SSS_SAMPLE_COUNT = 16;

    /**
     * Indicates that the irradiance texture is from the legacy GeometryBufferRenderer.
     * We use this to handle direct lights which don't render in the legacy GBuffer irradiance.
     */
    public USE_IRRADIANCE_TEXTURE_FOR_SCATTERING_GBUFFER = false;

    /**
     * Enables transmission slab
     */
    public TRANSMISSION_SLAB = false;

    /**
     * Enables transmission slab with volume
     */
    public TRANSMISSION_SLAB_VOLUME = false;

    /**
     * Enables subsurface slab
     */
    public SUBSURFACE_SLAB = false;

    /**
     * Enables thin-walled geometry
     */
    public GEOMETRY_THIN_WALLED = false;

    /**
     * Refraction of the 2D background texture. Might include the rest of the scene or just the background.
     */
    public REFRACTED_BACKGROUND = false;

    /**
     * Refraction of direct lights.
     */
    public REFRACTED_LIGHTS = false;

    /**
     * Refraction of the environment texture (IBL).
     */
    public REFRACTED_ENVIRONMENT = false;
    public REFRACTED_ENVIRONMENT_OPPOSITEZ = false;
    public REFRACTED_ENVIRONMENT_LOCAL_CUBE = false;

    public RADIANCEOCCLUSION = false;
    public HORIZONOCCLUSION = false;

    public INSTANCES = false;
    public THIN_INSTANCES = false;
    public INSTANCESCOLOR = false;

    public NUM_BONE_INFLUENCERS = 0;
    public BonesPerMesh = 0;
    public BONETEXTURE = false;
    public BONES_VELOCITY_ENABLED = false;

    public NONUNIFORMSCALING = false;

    public MORPHTARGETS = false;
    public MORPHTARGETS_POSITION = false;
    public MORPHTARGETS_NORMAL = false;
    public MORPHTARGETS_TANGENT = false;
    public MORPHTARGETS_UV = false;
    public MORPHTARGETS_UV2 = false;
    public MORPHTARGETS_COLOR = false;
    public MORPHTARGETTEXTURE_HASPOSITIONS = false;
    public MORPHTARGETTEXTURE_HASNORMALS = false;
    public MORPHTARGETTEXTURE_HASTANGENTS = false;
    public MORPHTARGETTEXTURE_HASUVS = false;
    public MORPHTARGETTEXTURE_HASUV2S = false;
    public MORPHTARGETTEXTURE_HASCOLORS = false;
    public NUM_MORPH_INFLUENCERS = 0;
    public MORPHTARGETS_TEXTURE = false;

    public USEPHYSICALLIGHTFALLOFF = false;
    public USEGLTFLIGHTFALLOFF = false;
    public TWOSIDEDLIGHTING = false;
    public MIRRORED = false;
    public SHADOWFLOAT = false;
    public CLIPPLANE = false;
    public CLIPPLANE2 = false;
    public CLIPPLANE3 = false;
    public CLIPPLANE4 = false;
    public CLIPPLANE5 = false;
    public CLIPPLANE6 = false;
    public POINTSIZE = false;
    public FOG = false;
    public LOGARITHMICDEPTH = false;
    public CAMERA_ORTHOGRAPHIC = false;
    public CAMERA_PERSPECTIVE = false;
    public AREALIGHTSUPPORTED = true;

    public FORCENORMALFORWARD = false;

    public SPECULARAA = false;

    public UNLIT = false;

    public DECAL_AFTER_DETAIL = false;

    public TEXTURE_REPETITION_MODE = 0;

    public DEBUGMODE = 0;
    public USE_VERTEX_PULLING = false;
    public VERTEX_PULLING_USE_INDEX_BUFFER = false;
    public VERTEX_PULLING_INDEX_BUFFER_32BITS = false;
    public RIGHT_HANDED = false;

    public CLUSTLIGHT_SLICES = 0;
    public CLUSTLIGHT_BATCH = 0;

    // BRDF defines
    BRDF_V_HEIGHT_CORRELATED = true;
    MS_BRDF_ENERGY_CONSERVATION = true;
    SPHERICAL_HARMONICS = true;
    SPECULAR_GLOSSINESS_ENERGY_CONSERVATION = true;
    MIX_IBL_RADIANCE_WITH_IRRADIANCE = true;
    LEGACY_SPECULAR_ENERGY_CONSERVATION = false;
    BASE_DIFFUSE_MODEL = Constants.MATERIAL_DIFFUSE_MODEL_E_OREN_NAYAR;
    DIELECTRIC_SPECULAR_MODEL = Constants.MATERIAL_DIELECTRIC_SPECULAR_MODEL_OPENPBR;
    CONDUCTOR_SPECULAR_MODEL = Constants.MATERIAL_CONDUCTOR_SPECULAR_MODEL_OPENPBR;

    /**
     * Initializes the PBR Material defines.
     * @param externalProperties The external properties
     */
    constructor(externalProperties?: { [name: string]: { type: string; default: any } }) {
        super(externalProperties);
        this.rebuild();
    }

    /**
     * Resets the PBR Material defines.
     */
    public override reset(): void {
        super.reset();
        this.ALPHATESTVALUE = "0.5";
        this.NORMALXYSCALE = true;
    }
}

class OpenPBRMaterialBase extends ImageProcessingMixin(PushMaterial) {}
/**
 * A Physically based material that follows the specification of OpenPBR.
 *
 * For more information, please refer to the documentation :
 * https://academysoftwarefoundation.github.io/OpenPBR/index.html
 */
export class OpenPBRMaterial extends OpenPBRMaterialBase {
    /**
     * Base Weight is a multiplier on the diffuse and metal lobes.
     * See OpenPBR's specs for base_weight
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor baseWeight: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _baseWeight: Property<number> = new Property<number>("base_weight", 1, "vBaseWeight", 1);

    /**
     * Base Weight is a multiplier on the diffuse and metal lobes.
     * See OpenPBR's specs for base_weight
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor baseWeightTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _baseWeightTexture: Sampler = new Sampler("base_weight", "baseWeight", "BASE_WEIGHT");

    /**
     * Color of the base diffuse lobe.
     * See OpenPBR's specs for base_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor baseColor: Color3;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _baseColor: Property<Color3> = new Property<Color3>("base_color", Color3.White(), "vBaseColor", 4);

    /**
     * Base Color Texture property.
     * See OpenPBR's specs for base_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor baseColorTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _baseColorTexture: Sampler = new Sampler("base_color", "baseColor", "BASE_COLOR");

    /**
     * Roughness of the diffuse lobe.
     * See OpenPBR's specs for base_diffuse_roughness
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor baseDiffuseRoughness: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _baseDiffuseRoughness: Property<number> = new Property<number>("base_diffuse_roughness", 0, "vBaseDiffuseRoughness", 1);

    /**
     * Roughness texture of the diffuse lobe.
     * See OpenPBR's specs for base_diffuse_roughness
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor baseDiffuseRoughnessTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _baseDiffuseRoughnessTexture: Sampler = new Sampler("base_diffuse_roughness", "baseDiffuseRoughness", "BASE_DIFFUSE_ROUGHNESS");

    /**
     * Metalness of the base lobe.
     * See OpenPBR's specs for base_metalness
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor baseMetalness: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _baseMetalness: Property<number> = new Property<number>("base_metalness", 0, "vReflectanceInfo", 4, 0);

    /**
     * Metalness texture.
     * See OpenPBR's specs for base_metalness
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor baseMetalnessTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _baseMetalnessTexture: Sampler = new Sampler("base_metalness", "baseMetalness", "BASE_METALNESS");

    /**
     * Weight of the specular lobe.
     * See OpenPBR's specs for specular_weight
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor specularWeight: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _specularWeight: Property<number> = new Property<number>("specular_weight", 1, "vReflectanceInfo", 4, 3);

    /**
     * Weight texture of the specular lobe.
     * See OpenPBR's specs for specular_weight
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor specularWeightTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _specularWeightTexture: Sampler = new Sampler("specular_weight", "specularWeight", "SPECULAR_WEIGHT");

    /**
     * Color of the specular lobe.
     * See OpenPBR's specs for specular_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor specularColor: Color3;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _specularColor: Property<Color3> = new Property<Color3>("specular_color", Color3.White(), "vSpecularColor", 4);

    /**
     * Specular Color Texture property.
     * See OpenPBR's specs for specular_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor specularColorTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _specularColorTexture: Sampler = new Sampler("specular_color", "specularColor", "SPECULAR_COLOR");

    /**
     * Roughness of the specular lobe.
     * See OpenPBR's specs for specular_roughness
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor specularRoughness: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _specularRoughness: Property<number> = new Property<number>("specular_roughness", 0.3, "vReflectanceInfo", 4, 1);

    /**
     * Roughness texture of the specular lobe.
     * See OpenPBR's specs for specular_roughness
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor specularRoughnessTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _specularRoughnessTexture: Sampler = new Sampler("specular_roughness", "specularRoughness", "SPECULAR_ROUGHNESS");

    /**
     * Anisotropic roughness of the specular lobe.
     * See OpenPBR's specs for specular_roughness_anisotropy
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor specularRoughnessAnisotropy: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _specularRoughnessAnisotropy: Property<number> = new Property<number>("specular_roughness_anisotropy", 0, "vSpecularAnisotropy", 3, 2);

    /**
     * Anisotropic Roughness texture.
     * See OpenPBR's specs for specular_roughness
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor specularRoughnessAnisotropyTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _specularRoughnessAnisotropyTexture: Sampler = new Sampler("specular_roughness_anisotropy", "specularRoughnessAnisotropy", "SPECULAR_ROUGHNESS_ANISOTROPY");

    /**
     * IOR of the specular lobe.
     * See OpenPBR's specs for specular_ior
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor specularIor: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _specularIor: Property<number> = new Property<number>("specular_ior", 1.5, "vReflectanceInfo", 4, 2);

    /**
     * Transmission weight of the surface.
     * See OpenPBR's specs for transmission_weight
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor transmissionWeight: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _transmissionWeight: Property<number> = new Property<number>("transmission_weight", 0.0, "vTransmissionWeight", 1);

    /**
     * Transmission weight texture.
     * See OpenPBR's specs for transmission_weight
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor transmissionWeightTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _transmissionWeightTexture: Sampler = new Sampler("transmission_weight", "transmissionWeight", "TRANSMISSION_WEIGHT");

    /**
     * Transmission color of the surface.
     * See OpenPBR's specs for transmission_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor transmissionColor: Color3;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _transmissionColor: Property<Color3> = new Property<Color3>("transmission_color", Color3.White(), "vTransmissionColor", 3, 0);

    /**
     * Transmission color texture.
     * See OpenPBR's specs for transmission_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor transmissionColorTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _transmissionColorTexture: Sampler = new Sampler("transmission_color", "transmissionColor", "TRANSMISSION_COLOR");

    /**
     * Transmission depth of the volume
     * See OpenPBR's specs for transmission_depth
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor transmissionDepth: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _transmissionDepth: Property<number> = new Property<number>("transmission_depth", 0.0, "vTransmissionDepth", 1, 0);

    /**
     * Transmission depth texture.
     * See OpenPBR's specs for transmission_depth
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor transmissionDepthTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _transmissionDepthTexture: Sampler = new Sampler("transmission_depth", "transmissionDepth", "TRANSMISSION_DEPTH");

    /**
     * Transmission scatter of the surface.
     * See OpenPBR's specs for transmission_scatter
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor transmissionScatter: Color3;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _transmissionScatter: Property<Color3> = new Property<Color3>("transmission_scatter", Color3.Black(), "vTransmissionScatter", 3, 0);

    /**
     * Transmission scatter texture.
     * See OpenPBR's specs for transmission_scatter
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor transmissionScatterTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _transmissionScatterTexture: Sampler = new Sampler("transmission_scatter", "transmissionScatter", "TRANSMISSION_SCATTER");

    /**
     * Transmission scatter anisotropy
     * See OpenPBR's specs for transmission_scatter_anisotropy
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor transmissionScatterAnisotropy: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _transmissionScatterAnisotropy: Property<number> = new Property<number>("transmission_scatter_anisotropy", 0.0, "vTransmissionScatterAnisotropy", 1, 0);

    /**
     * Transmission Dispersion Scale factor.
     * See OpenPBR's specs for transmission_dispersion_scale
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor transmissionDispersionScale: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _transmissionDispersionScale: Property<number> = new Property<number>("transmission_dispersion_scale", 0.0, "vTransmissionDispersionScale", 1, 0);

    /**
     * Transmission Dispersion Scale texture.
     * See OpenPBR's specs for transmission_dispersion_scale
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor transmissionDispersionScaleTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _transmissionDispersionScaleTexture: Sampler = new Sampler("transmission_dispersion_scale", "transmissionDispersionScale", "TRANSMISSION_DISPERSION_SCALE");

    /**
     * Transmission Dispersion Abbe number.
     * See OpenPBR's specs for transmission_dispersion_abbe_number
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor transmissionDispersionAbbeNumber: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _transmissionDispersionAbbeNumber: Property<number> = new Property<number>("transmission_dispersion_abbe_number", 20.0, "vTransmissionDispersionAbbeNumber", 1, 0);

    /**
     * Defines the amount of subsurface scattering on the surface.
     * See OpenPBR's specs for subsurface_weight
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor subsurfaceWeight: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _subsurfaceWeight: Property<number> = new Property<number>("subsurface_weight", 0.0, "vSubsurfaceWeight", 1, 0, "SUBSURFACE_SLAB");

    /**
     * Subsurface weight texture.
     * See OpenPBR's specs for subsurface_weight
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor subsurfaceWeightTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _subsurfaceWeightTexture: Sampler = new Sampler("subsurface_weight", "subsurfaceWeight", "SUBSURFACE_WEIGHT");

    /**
     * Defines the color of the subsurface scattering in the volume.
     * See OpenPBR's specs for subsurface_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor subsurfaceColor: Color3;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _subsurfaceColor: Property<Color3> = new Property<Color3>("subsurface_color", new Color3(0.8, 0.8, 0.8), "vSubsurfaceColor", 3, 0, "SUBSURFACE_SLAB");

    /**
     * Subsurface color texture.
     * See OpenPBR's specs for subsurface_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor subsurfaceColorTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _subsurfaceColorTexture: Sampler = new Sampler("subsurface_color", "subsurfaceColor", "SUBSURFACE_COLOR");

    /**
     * Defines the radius of the subsurface scattering in the volume.
     * See OpenPBR's specs for subsurface_radius
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor subsurfaceRadius: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _subsurfaceRadius: Property<number> = new Property<number>("subsurface_radius", 0.1, "vSubsurfaceRadius", 1, 0, "SUBSURFACE_SLAB");

    /**
     * Defines the scale factor applied to the subsurface radius.
     * See OpenPBR's specs for subsurface_radius_scale
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor subsurfaceRadiusScale: Color3;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _subsurfaceRadiusScale: Property<Color3> = new Property<Color3>("subsurface_radius_scale", new Color3(1, 0.5, 0.25), "vSubsurfaceRadiusScale", 3, 0, "SUBSURFACE_SLAB");

    /**
     * Subsurface radius scale texture.
     * See OpenPBR's specs for subsurface_radius_scale
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor subsurfaceRadiusScaleTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _subsurfaceRadiusScaleTexture: Sampler = new Sampler("subsurface_radius_scale", "subsurfaceRadiusScale", "SUBSURFACE_RADIUS_SCALE");

    /**
     * Defines the anisotropy of the subsurface scattering in the volume.
     * See OpenPBR's specs for subsurface_scatter_anisotropy
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor subsurfaceScatterAnisotropy: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _subsurfaceScatterAnisotropy: Property<number> = new Property<number>("subsurface_scatter_anisotropy", 0.0, "vSubsurfaceScatterAnisotropy", 1, 0, "SUBSURFACE_SLAB");

    /**
     * Defines the amount of clear coat on the surface.
     * See OpenPBR's specs for coat_weight
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor coatWeight: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _coatWeight: Property<number> = new Property<number>("coat_weight", 0.0, "vCoatWeight", 1, 0);

    /**
     * Coat weight texture.
     * See OpenPBR's specs for coat_weight
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor coatWeightTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _coatWeightTexture: Sampler = new Sampler("coat_weight", "coatWeight", "COAT_WEIGHT");

    /**
     * Defines the color of the clear coat on the surface.
     * See OpenPBR's specs for coat_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor coatColor: Color3;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _coatColor: Property<Color3> = new Property<Color3>("coat_color", Color3.White(), "vCoatColor", 3, 0);

    /**
     * Color texture of the clear coat.
     * See OpenPBR's specs for coat_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor coatColorTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _coatColorTexture: Sampler = new Sampler("coat_color", "coatColor", "COAT_COLOR");

    /**
     * Defines the roughness of the clear coat on the surface.
     * See OpenPBR's specs for coat_roughness
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor coatRoughness: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _coatRoughness: Property<number> = new Property<number>("coat_roughness", 0.0, "vCoatRoughness", 1, 0);

    /**
     * Roughness texture of the clear coat.
     * See OpenPBR's specs for coat_roughness
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor coatRoughnessTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _coatRoughnessTexture: Sampler = new Sampler("coat_roughness", "coatRoughness", "COAT_ROUGHNESS");

    /**
     * Defines the anisotropy of the clear coat on the surface.
     * See OpenPBR's specs for coat_roughness_anisotropy
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor coatRoughnessAnisotropy: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _coatRoughnessAnisotropy: Property<number> = new Property<number>("coat_roughness_anisotropy", 0, "vCoatRoughnessAnisotropy", 1);

    /**
     * Anisotropic Roughness texture of the clear coat.
     * See OpenPBR's specs for coat_roughness_anisotropy
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor coatRoughnessAnisotropyTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _coatRoughnessAnisotropyTexture: Sampler = new Sampler("coat_roughness_anisotropy", "coatRoughnessAnisotropy", "COAT_ROUGHNESS_ANISOTROPY");

    /**
     * Defines the IOR of the clear coat on the surface.
     * See OpenPBR's specs for coat_ior
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor coatIor: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _coatIor: Property<number> = new Property<number>("coat_ior", 1.5, "vCoatIor", 1, 0);

    /**
     * Defines the amount that interreflections within the coat allow the underlying surface
     * to be darkened. A value of 1.0 means that the physically correct amount of darkening
     * is applied, while a value of 0.0 means that no darkening is applied.
     * See OpenPBR's specs for coat_darkening
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor coatDarkening: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _coatDarkening: Property<number> = new Property<number>("coat_darkening", 1.0, "vCoatDarkening", 1, 0);

    /**
     * Defines the amount that interreflections within the coat allow the underlying surface
     * to be darkened. A value of 1.0 means that the physically correct amount of darkening
     * is applied, while a value of 0.0 means that no darkening is applied.
     * See OpenPBR's specs for coat_darkening
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor coatDarkeningTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _coatDarkeningTexture: Sampler = new Sampler("coat_darkening", "coatDarkening", "COAT_DARKENING");

    /**
     * Specifies whether the coat roughness is taken from the
     * same texture as the coat_weight.
     */
    public useCoatRoughnessFromWeightTexture: boolean = false;

    /**
     * Defines the weight of the fuzz layer on the surface.
     * See OpenPBR's specs for fuzz_weight
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor fuzzWeight: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _fuzzWeight: Property<number> = new Property<number>("fuzz_weight", 0.0, "vFuzzWeight", 1, 0);

    /**
     * Weight texture of the fuzz layer.
     * See OpenPBR's specs for fuzz_weight
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor fuzzWeightTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _fuzzWeightTexture: Sampler = new Sampler("fuzz_weight", "fuzzWeight", "FUZZ_WEIGHT");

    /**
     * Defines the color of the fuzz layer on the surface.
     * See OpenPBR's specs for fuzz_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor fuzzColor: Color3;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _fuzzColor: Property<Color3> = new Property<Color3>("fuzz_color", Color3.White(), "vFuzzColor", 3, 0);

    /**
     * Color texture of the fuzz layer.
     * See OpenPBR's specs for fuzz_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor fuzzColorTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _fuzzColorTexture: Sampler = new Sampler("fuzz_color", "fuzzColor", "FUZZ_COLOR");

    /**
     * Defines the roughness of the fuzz layer on the surface.
     * See OpenPBR's specs for fuzz_roughness
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor fuzzRoughness: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _fuzzRoughness: Property<number> = new Property<number>("fuzz_roughness", 0.5, "vFuzzRoughness", 1, 0);

    /**
     * Roughness texture of the fuzz layer.
     * See OpenPBR's specs for fuzz_roughness
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor fuzzRoughnessTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _fuzzRoughnessTexture: Sampler = new Sampler("fuzz_roughness", "fuzzRoughness", "FUZZ_ROUGHNESS");

    /**
     * Defines whether the geometry is thin-walled (like a sheet of paper) or not.
     * See OpenPBR's specs for geometry_thin_walled
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor geometryThinWalled: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _geometryThinWalled: Property<number> = new Property<number>("geometry_thin_walled", 0, "vGeometryThinWalled", 1, 0);

    /**
     * Defines the normal of the material's geometry.
     * See OpenPBR's specs for geometry_normal
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor geometryNormalTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _geometryNormalTexture: Sampler = new Sampler("geometry_normal", "geometryNormal", "GEOMETRY_NORMAL");

    /**
     * Defines the tangent of the material's geometry. Used only for anisotropic reflections.
     * See OpenPBR's specs for geometry_tangent
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor geometryTangent: Vector2;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _geometryTangent: Property<Vector2> = new Property<Vector2>("geometry_tangent", new Vector2(1, 0), "vSpecularAnisotropy", 3, 0);

    /**
     * Defines the angle of the tangent of the material's geometry. Used only for anisotropic reflections.
     * See OpenPBR's specs for geometry_tangent
     */
    public get geometryTangentAngle(): number {
        return Math.atan2(this.geometryTangent.y, this.geometryTangent.x);
    }

    public set geometryTangentAngle(value: number) {
        this.geometryTangent = new Vector2(Math.cos(value), Math.sin(value));
    }

    /**
     * Defines the tangent of the material's geometry. Used only for anisotropic reflections.
     * See OpenPBR's specs for geometry_tangent
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor geometryTangentTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _geometryTangentTexture: Sampler = new Sampler("geometry_tangent", "geometryTangent", "GEOMETRY_TANGENT");

    /**
     * Defines the normal of the material's coat layer.
     * See OpenPBR's specs for geometry_coat_normal
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor geometryCoatNormalTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _geometryCoatNormalTexture: Sampler = new Sampler("geometry_coat_normal", "geometryCoatNormal", "GEOMETRY_COAT_NORMAL");

    /**
     * Defines the tangent of the material's coat layer. Used only for anisotropic reflections.
     * See OpenPBR's specs for geometry_coat_tangent
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor geometryCoatTangent: Vector2;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _geometryCoatTangent: Property<Vector2> = new Property<Vector2>("geometry_coat_tangent", new Vector2(1, 0), "vGeometryCoatTangent", 2, 0);

    /**
     * Defines the angle of the tangent of the material's coat layer.
     */
    public get geometryCoatTangentAngle(): number {
        return Math.atan2(this.geometryCoatTangent.y, this.geometryCoatTangent.x);
    }

    /**
     * Defines the angle of the tangent of the material's coat layer.
     */
    public set geometryCoatTangentAngle(value: number) {
        this.geometryCoatTangent = new Vector2(Math.cos(value), Math.sin(value));
    }

    /**
     * Defines the tangent of the material's coat layer. Used only for anisotropic reflections.
     * See OpenPBR's specs for geometry_coat_tangent
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor geometryCoatTangentTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _geometryCoatTangentTexture: Sampler = new Sampler("geometry_coat_tangent", "geometryCoatTangent", "GEOMETRY_COAT_TANGENT");

    /**
     * Defines the opacity of the material's geometry.
     * See OpenPBR's specs for geometry_opacity
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor geometryOpacity: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _geometryOpacity: Property<number> = new Property<number>("geometry_opacity", 1.0, "vBaseColor", 4, 3);

    /**
     * Defines the opacity texture of the material's geometry.
     * See OpenPBR's specs for geometry_opacity
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor geometryOpacityTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _geometryOpacityTexture: Sampler = new Sampler("geometry_opacity", "geometryOpacity", "GEOMETRY_OPACITY");

    /**
     * Defines the thickness of the material's geometry.
     * Not part of OpenPBR's specs but useful for rasterization approximations of volume.
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor geometryThickness: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _geometryThickness: Property<number> = new Property<number>("geometry_thickness", 0.0, "vGeometryThickness", 1, 0);

    /**
     * Defines the thickness of the material's geometry.
     * Not part of OpenPBR's specs but useful for rasterization approximations of volume.
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor geometryThicknessTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _geometryThicknessTexture: Sampler = new Sampler("geometry_thickness", "geometryThickness", "GEOMETRY_THICKNESS");

    /**
     * Defines the luminance of the material's emission.
     * See OpenPBR's specs for emission_luminance
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor emissionLuminance: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _emissionLuminance: Property<number> = new Property<number>("emission_luminance", 1.0, "vLightingIntensity", 4, 1);

    /**
     * Defines the color of the material's emission.
     * See OpenPBR's specs for emission_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor emissionColor: Color3;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _emissionColor: Property<Color3> = new Property<Color3>("emission_color", Color3.Black(), "vEmissionColor", 3);

    /**
     * Defines the texture of the material's emission color.
     * See OpenPBR's specs for emission_color
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor emissionColorTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _emissionColorTexture: Sampler = new Sampler("emission_color", "emissionColor", "EMISSION_COLOR");

    /**
     * Defines the weight of the thin film layer on top of the base layer for iridescent effects.
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor thinFilmWeight: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _thinFilmWeight: Property<number> = new Property<number>("thin_film_weight", 0.0, "vThinFilmWeight", 1, 0);

    /**
     * Thin film weight texture.
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor thinFilmWeightTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _thinFilmWeightTexture: Sampler = new Sampler("thin_film_weight", "thinFilmWeight", "THIN_FILM_WEIGHT");

    /**
     * Defines the thickness of the thin film layer in μm. If a texture is provided for thinFilmWeightTexture,
     * this value will act as a multiplier to the texture values.
     * See OpenPBR's specs for thin_film_thickness
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor thinFilmThickness: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _thinFilmThickness: Property<number> = new Property<number>("thin_film_thickness", 0.5, "vThinFilmThickness", 2, 0);

    /**
     * Defines the minimum thickness of the thin film layer in μm.
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor thinFilmThicknessMin: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _thinFilmThicknessMin: Property<number> = new Property<number>("thin_film_thickness_min", 0.0, "vThinFilmThickness", 2, 1);

    /**
     * Defines the maximum thickness of the thin film layer in μm.
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor thinFilmThicknessTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _thinFilmThicknessTexture: Sampler = new Sampler("thin_film_thickness", "thinFilmThickness", "THIN_FILM_THICKNESS");

    /**
     * Defines the index of refraction of the thin film layer.
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor thinFilmIor: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _thinFilmIor: Property<number> = new Property<number>("thin_film_ior", 1.4, "vThinFilmIor", 1, 0);

    /**
     * Defines the ambient occlusion texture.
     */
    @addAccessorsForMaterialProperty("_markAllSubMeshesAsTexturesDirty")
    accessor ambientOcclusionTexture: Nullable<BaseTexture>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _ambientOcclusionTexture: Sampler = new Sampler("ambient_occlusion", "ambientOcclusion", "AMBIENT_OCCLUSION");

    /** SSS convolution uses 8 samples. */
    public static readonly SSS_QUALITY_LOW = 0;
    /** SSS convolution uses 16 samples (default). */
    public static readonly SSS_QUALITY_MEDIUM = 1;
    /** SSS convolution uses 32 samples. */
    public static readonly SSS_QUALITY_HIGH = 2;

    private _sssQuality: number = OpenPBRMaterial.SSS_QUALITY_MEDIUM;
    /**
     * Controls the sample count of the screen-space SSS convolution kernel.
     * Use the SSS_QUALITY_LOW / MEDIUM / HIGH constants (8 / 16 / 32 samples).
     * Higher quality reduces noise at the cost of GPU time. Default: MEDIUM.
     */
    public get sssQuality(): number {
        return this._sssQuality;
    }
    public set sssQuality(value: number) {
        if (this._sssQuality !== value) {
            this._sssQuality = value;
            this.markAsDirty(Constants.MATERIAL_TextureDirtyFlag);
        }
    }

    private _sssIrradianceTexture: Nullable<ThinTexture> = null;
    /**
     * Defines the irradiance texture used for subsurface scattering.
     * If it's not provided, the irradiance will be looked for in the scene.geometryBufferRenderer.
     * Accepts a {@link ThinTexture} so that an {@link InternalTexture} obtained from a frame graph
     * handle can be wrapped with `new ThinTexture(internalTexture)` and assigned directly.
     * Setting this property marks all sub-meshes as textures-dirty so the shader recompiles.
     */
    public get sssIrradianceTexture(): Nullable<ThinTexture> {
        return this._sssIrradianceTexture;
    }
    public set sssIrradianceTexture(value: Nullable<ThinTexture>) {
        if (this._sssIrradianceTexture === value) {
            return;
        }
        this._sssIrradianceTexture = value;
        this._markAllSubMeshesAsTexturesDirty();
    }

    private _sssDepthTexture: Nullable<ThinTexture> = null;
    /**
     * Defines the depth texture used for subsurface scattering. This is the depth defined
     * in screen space. If it's not provided, the depth will be looked for in the scene.geometryBufferRenderer.
     * Accepts a {@link ThinTexture} so that an {@link InternalTexture} obtained from a frame graph
     * handle can be wrapped with `new ThinTexture(internalTexture)` and assigned directly.
     * Setting this property marks all sub-meshes as textures-dirty so the shader recompiles.
     */
    public get sssDepthTexture(): Nullable<ThinTexture> {
        return this._sssDepthTexture;
    }
    public set sssDepthTexture(value: Nullable<ThinTexture>) {
        if (this._sssDepthTexture === value) {
            return;
        }
        this._sssDepthTexture = value;
        this._markAllSubMeshesAsTexturesDirty();
    }

    private _propertyList: { [name: string]: Property<any> };
    private _uniformsList: { [name: string]: Uniform } = {};
    /**
     * Flat array view of `_uniformsList`, populated once at construction. Used
     * by the per-frame bind loop to avoid `Object.values()` allocation and
     * closure creation on every submesh binding.
     */
    private _uniformsArray: Uniform[] = [];
    private _samplersList: { [name: string]: Sampler } = {};
    private _samplerDefines: { [name: string]: { type: string; default: any } } = {};

    private static _noiseTextures: { [id: number]: BaseTexture } = {};

    /**
     * Intensity of the direct lights e.g. the four lights available in your scene.
     * This impacts both the direct diffuse and specular highlights.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor directIntensity: number = 1.0;

    /**
     * Intensity of the environment e.g. how much the environment will light the object
     * either through harmonics for rough material or through the reflection for shiny ones.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor environmentIntensity: number = 1.0;

    /**
     * Specifies that the specular weight is stored in the alpha channel of the specular weight texture.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor useSpecularWeightFromTextureAlpha = false;

    /**
     * Enforces alpha test in opaque or blend mode in order to improve the performances of some situations.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesAndMiscDirty")
    public accessor forceAlphaTest = false;

    /**
     * Defines the alpha limits in alpha test mode.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesAndMiscDirty")
    public accessor alphaCutOff = 0.4;

    /**
     * Specifies if the metallic texture contains the ambient occlusion information in its red channel.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor useAmbientOcclusionFromMetallicTextureRed = false;

    /**
     * Specifies if the ambient texture contains the ambient occlusion information in its red channel only.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor useAmbientInGrayScale = false;

    /**
     * Specifies if we can see through the surface of the material due to subsurface scattering or transmission.
     */
    public get hasTransparency(): boolean {
        return this.subsurfaceWeight > 0 || this.transmissionWeight > 0;
    }

    /** Specifies if the material has scattering properties such as subsurface scattering or transmission scattering. */
    public get hasScattering(): boolean {
        return (this.transmissionWeight > 0 && this.transmissionDepth > 0 && !this.transmissionScatter.equals(Color3.BlackReadOnly)) || this.subsurfaceWeight > 0;
    }

    /**
     * BJS is using an hardcoded light falloff based on a manually sets up range.
     * In PBR, one way to represents the falloff is to use the inverse squared root algorithm.
     * This parameter can help you switch back to the BJS mode in order to create scenes using both materials.
     */
    @serialize()
    public get usePhysicalLightFalloff(): boolean {
        return this._lightFalloff === Material.LIGHTFALLOFF_PHYSICAL;
    }

    /**
     * BJS is using an hardcoded light falloff based on a manually sets up range.
     * In PBR, one way to represents the falloff is to use the inverse squared root algorithm.
     * This parameter can help you switch back to the BJS mode in order to create scenes using both materials.
     */
    public set usePhysicalLightFalloff(value: boolean) {
        if (value !== this.usePhysicalLightFalloff) {
            // Ensure the effect will be rebuilt.
            this._markAllSubMeshesAsTexturesDirty();

            if (value) {
                this._lightFalloff = Material.LIGHTFALLOFF_PHYSICAL;
            } else {
                this._lightFalloff = Material.LIGHTFALLOFF_STANDARD;
            }
        }
    }

    /**
     * In order to support the falloff compatibility with gltf, a special mode has been added
     * to reproduce the gltf light falloff.
     */
    @serialize()
    public get useGLTFLightFalloff(): boolean {
        return this._lightFalloff === Material.LIGHTFALLOFF_GLTF;
    }

    /**
     * In order to support the falloff compatibility with gltf, a special mode has been added
     * to reproduce the gltf light falloff.
     */
    public set useGLTFLightFalloff(value: boolean) {
        if (value !== this.useGLTFLightFalloff) {
            // Ensure the effect will be rebuilt.
            this._markAllSubMeshesAsTexturesDirty();

            if (value) {
                this._lightFalloff = Material.LIGHTFALLOFF_GLTF;
            } else {
                this._lightFalloff = Material.LIGHTFALLOFF_STANDARD;
            }
        }
    }

    /**
     * Allows using an object space normal map (instead of tangent space).
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor useObjectSpaceNormalMap = false;

    /**
     * Allows using the normal map in parallax mode.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor useParallax = false;

    /**
     * Allows using the normal map in parallax occlusion mode.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor useParallaxOcclusion = false;

    /**
     * Controls the scale bias of the parallax mode.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor parallaxScaleBias = 0.05;

    /**
     * If sets to true, disables all the lights affecting the material.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsLightsDirty")
    public accessor disableLighting = false;

    /**
     * Force the shader to compute irradiance in the fragment shader in order to take normal mapping into account.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor forceIrradianceInFragment = false;

    /**
     * Number of Simultaneous lights allowed on the material.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsLightsDirty")
    public accessor maxSimultaneousLights = 4;

    /**
     * If sets to true, x component of normal map value will invert (x = 1.0 - x).
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor invertNormalMapX = false;

    /**
     * If sets to true, y component of normal map value will invert (y = 1.0 - y).
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor invertNormalMapY = false;

    /**
     * If sets to true and backfaceCulling is false, normals will be flipped on the backside.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor twoSidedLighting = false;

    /**
     * A fresnel is applied to the alpha of the model to ensure grazing angles edges are not alpha tested.
     * And/Or occlude the blended part. (alpha is converted to gamma to compute the fresnel)
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor useAlphaFresnel = false;

    /**
     * A fresnel is applied to the alpha of the model to ensure grazing angles edges are not alpha tested.
     * And/Or occlude the blended part. (alpha stays linear to compute the fresnel)
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor useLinearAlphaFresnel = false;

    /**
     * Let user defines the brdf lookup texture used for IBL.
     * A default 8bit version is embedded but you could point at :
     * * Default texture: https://assets.babylonjs.com/environments/correlatedMSBRDF_RGBD.png
     * * Default 16bit pixel depth texture: https://assets.babylonjs.com/environments/correlatedMSBRDF.dds
     * * LEGACY Default None correlated https://assets.babylonjs.com/environments/uncorrelatedBRDF_RGBD.png
     * * LEGACY Default None correlated 16bit pixel depth https://assets.babylonjs.com/environments/uncorrelatedBRDF.dds
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor environmentBRDFTexture: Nullable<BaseTexture> = null;

    /**
     * Force normal to face away from face.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor forceNormalForward = false;

    /**
     * Enables specular anti aliasing in the PBR shader.
     * It will both interacts on the Geometry for analytical and IBL lighting.
     * It also prefilter the roughness map based on the normalmap values.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor enableSpecularAntiAliasing = false;

    /**
     * This parameters will enable/disable Horizon occlusion to prevent normal maps to look shiny when the normal
     * makes the reflect vector face the model (under horizon).
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor useHorizonOcclusion = true;

    /**
     * This parameters will enable/disable radiance occlusion by preventing the radiance to lit
     * too much the area relying on ambient texture to define their ambient occlusion.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor useRadianceOcclusion = true;

    /**
     * If set to true, no lighting calculations will be applied.
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsMiscDirty")
    public accessor unlit = false;

    /**
     * If sets to true, the decal map will be applied after the detail map. Else, it is applied before (default: false)
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsMiscDirty")
    public accessor applyDecalMapAfterDetailMap = false;

    /**
     * Force all the PBR materials to compile to glsl even on WebGPU engines.
     * False by default. This is mostly meant for backward compatibility.
     */
    public static ForceGLSL = false;

    /**
     * This stores the direct, emissive, environment, and specular light intensities into a Vector4.
     */
    private _lightingInfos: Vector4 = new Vector4(this.directIntensity, 1.0, this.environmentIntensity, 1.0);

    /**
     * Stores the radiance (and, possibly, irradiance) values in a texture.
     * @internal
     */
    public _radianceTexture: Nullable<BaseTexture> = null;

    /**
     * Specifies that the specular weight will be read from the alpha channel.
     * This is for compatibility with glTF's KHR_materials_specular extension.
     * @internal
     */
    public _useSpecularWeightFromAlpha = false;

    /**
     * Specifies that the specular weight will be read from the alpha channel of the specular color texture.
     * This is for compatibility with glTF's KHR_materials_specular extension.
     * @internal
     */
    public _useSpecularWeightFromSpecularColorTexture = false;

    /**
     * Specifies if the material uses anisotropy weight read from the geometry tangent texture's blue channel.
     * This is for compatibility with glTF's anisotropy extension.
     * @internal
     */
    public _useSpecularRoughnessAnisotropyFromTangentTexture = false;

    /**
     * Specifies if the material uses coat anisotropy weight read from the coat's geometry tangent texture's blue channel.
     * This is for compatibility with glTF's clearcoat_anisotropy extension.
     * @internal
     */
    public _useCoatRoughnessAnisotropyFromTangentTexture = false;

    /**
     * Specifies whether the coat roughness is taken from the green channel of the coat texture.
     * This is for compatibility with glTF's KHR_materials_clearcoat and KHR_materials_coat extensions.
     * @internal
     */
    public _useCoatRoughnessFromGreenChannel = false;

    /**
     * Assume the anisotropy data is stored in the format specified by
     * KHR_materials_anisotropy.
     * @internal
     */
    public _useGltfStyleAnisotropy = false;

    /**
     * Specifies that the fuzz roughness is stored in the alpha channel of the texture.
     * This is for compatibility with glTF where the fuzz roughness is often stored in
     * the alpha channel of the fuzz color texture.
     */
    public _useFuzzRoughnessFromTextureAlpha = false;

    /**
     * Specifies that the subsurface weight is stored in the alpha channel of the texture.
     * This is for compatibility with glTF where the subsurface weight is stored in
     * the alpha channel of the diffuseTransmissionTexture.
     */
    public _useSubsurfaceWeightFromTextureAlpha = false;

    /**
     * This parameters will enable/disable Horizon occlusion to prevent normal maps to look shiny when the normal
     * makes the reflect vector face the model (under horizon).
     * @internal
     */
    public _useHorizonOcclusion = true;

    /**
     * This parameters will enable/disable radiance occlusion by preventing the radiance to lit
     * too much the area relying on ambient texture to define their ambient occlusion.
     * @internal
     */
    public _useRadianceOcclusion = true;

    /**
     * Specifies that the alpha is coming from the base color texture's alpha channel.
     * This is for compatibility with glTF.
     * @internal
     */
    public _useAlphaFromBaseColorTexture = false;

    /**
     * Specifies if the metallic texture contains the ambient occlusion information in its red channel.
     * This is for compatibility with glTF.
     * @internal
     */
    public _useAmbientOcclusionFromMetallicTextureRed = false;

    /**
     * Specifies if the metallic texture contains the roughness information in its green channel.
     * This is for compatibility with glTF.
     * @internal
     */
    public _useRoughnessFromMetallicTextureGreen = false;

    /**
     * Specifies if the metallic texture contains the metallic information in its blue channel.
     * This is for compatibility with glTF.
     * @internal
     */
    public _useMetallicFromMetallicTextureBlue = false;

    /**
     * Specifies if the thin film thickness is stored in the green channel of the thin film thickness texture.
     * This is for compatibility with glTF.
     * @internal
     */
    public _useThinFilmThicknessFromTextureGreen = false;

    /**
     * Specifies if the geometry thickness is stored in the green channel of the geometry thickness texture.
     * This is for compatibility with glTF.
     * @internal
     */
    public _useGeometryThicknessFromGreenChannel = false;

    /**
     * Defines the  falloff type used in this material.
     * It by default is Physical.
     * @internal
     */
    public _lightFalloff = Material.LIGHTFALLOFF_PHYSICAL;

    /**
     * Allows using an object space normal map (instead of tangent space).
     * @internal
     */
    public _useObjectSpaceNormalMap = false;

    /**
     * Allows using the normal map in parallax mode.
     * @internal
     */
    public _useParallax = false;

    /**
     * Allows using the normal map in parallax occlusion mode.
     * @internal
     */
    public _useParallaxOcclusion = false;

    /**
     * Controls the scale bias of the parallax mode.
     * @internal
     */
    public _parallaxScaleBias = 0.05;

    /**
     * If sets to true, disables all the lights affecting the material.
     * @internal
     */
    public _disableLighting = false;

    /**
     * Number of Simultaneous lights allowed on the material.
     * @internal
     */
    public _maxSimultaneousLights = 4;

    /**
     * If sets to true, x component of normal map value will be inverted (x = 1.0 - x).
     * @internal
     */
    public _invertNormalMapX = false;

    /**
     * If sets to true, y component of normal map value will be inverted (y = 1.0 - y).
     * @internal
     */
    public _invertNormalMapY = false;

    /**
     * If sets to true and backfaceCulling is false, normals will be flipped on the backside.
     * @internal
     */
    public _twoSidedLighting = false;

    /**
     * Defines the alpha limits in alpha test mode.
     * @internal
     */
    public _alphaCutOff = 0.4;

    /**
     * A fresnel is applied to the alpha of the model to ensure grazing angles edges are not alpha tested.
     * And/Or occlude the blended part. (alpha is converted to gamma to compute the fresnel)
     * @internal
     */
    public _useAlphaFresnel = false;

    /**
     * A fresnel is applied to the alpha of the model to ensure grazing angles edges are not alpha tested.
     * And/Or occlude the blended part. (alpha stays linear to compute the fresnel)
     * @internal
     */
    public _useLinearAlphaFresnel = false;

    /**
     * Specifies the environment BRDF texture used to compute the scale and offset roughness values
     * from cos theta and roughness:
     * http://blog.selfshadow.com/publications/s2013-shading-course/karis/s2013_pbs_epic_notes_v2.pdf
     * @internal
     */
    public _environmentBRDFTexture: Nullable<BaseTexture> = null;

    /**
     * Specifies the environment BRDF texture used to compute the scale and offset roughness values
     * from cos theta and roughness for the fuzz layer:
     * https://github.com/tizian/ltc-sheen?tab=readme-ov-file
     * @internal
     */
    public _environmentFuzzBRDFTexture: Nullable<BaseTexture> = null;

    private _backgroundRefractionTexture: Nullable<BaseTexture> = null;
    /**
     * Set the texture used for refraction of the background of transparent materials
     * @internal
     */
    public get backgroundRefractionTexture(): Nullable<BaseTexture> {
        return this._backgroundRefractionTexture;
    }
    public set backgroundRefractionTexture(texture: Nullable<BaseTexture>) {
        this._backgroundRefractionTexture = texture;
        this._markAllSubMeshesAsTexturesDirty();
    }

    private _refractionHighQualityBlur: boolean = true;
    /**
     * When true, uses a 4-tap rotated-grid kernel for refractive background blur,
     * eliminating bilinear block artifacts at the cost of 3 extra texture samples.
     * When false, a single dithered sample is used. Default: true.
     */
    public get refractionHighQualityBlur(): boolean {
        return this._refractionHighQualityBlur;
    }
    public set refractionHighQualityBlur(value: boolean) {
        if (this._refractionHighQualityBlur !== value) {
            this._refractionHighQualityBlur = value;
            this.markAsDirty(Constants.MATERIAL_TextureDirtyFlag);
        }
    }

    /**
     * Force the shader to compute irradiance in the fragment shader in order to take normal mapping into account.
     * @internal
     */
    public _forceIrradianceInFragment = false;

    private _realTimeFiltering: boolean = false;
    /**
     * Enables realtime filtering on the texture.
     */
    public get realTimeFiltering() {
        return this._realTimeFiltering;
    }
    public set realTimeFiltering(b: boolean) {
        this._realTimeFiltering = b;
        this.markAsDirty(Constants.MATERIAL_TextureDirtyFlag);
    }

    private _realTimeFilteringQuality: number = Constants.TEXTURE_FILTERING_QUALITY_LOW;
    /**
     * Quality switch for realtime filtering
     */
    public get realTimeFilteringQuality(): number {
        return this._realTimeFilteringQuality;
    }
    public set realTimeFilteringQuality(n: number) {
        this._realTimeFilteringQuality = n;
        this.markAsDirty(Constants.MATERIAL_TextureDirtyFlag);
    }

    private _fuzzSampleNumber: number = 4;

    /**
     * The number of samples used to compute the fuzz IBL lighting.
     */
    public get fuzzSampleNumber(): number {
        return this._fuzzSampleNumber;
    }
    public set fuzzSampleNumber(n: number) {
        this._fuzzSampleNumber = n;
        this.markAsDirty(Constants.MATERIAL_TextureDirtyFlag);
    }

    /**
     * Can this material render to several textures at once
     */
    public override get canRenderToMRT() {
        return true;
    }

    /**
     * Force normal to face away from face.
     * @internal
     */
    public _forceNormalForward = false;

    /**
     * Enables specular anti aliasing in the PBR shader.
     * It will both interacts on the Geometry for analytical and IBL lighting.
     * It also prefilter the roughness map based on the normalmap values.
     * @internal
     */
    public _enableSpecularAntiAliasing = false;

    /**
     * Stores the available render targets.
     */
    private _renderTargets = new SmartArray<RenderTargetTexture>(16);

    /**
     * If set to true, no lighting calculations will be applied.
     */
    private _unlit = false;

    /**
     * If sets to true, the decal map will be applied after the detail map. Else, it is applied before (default: false)
     */
    private _applyDecalMapAfterDetailMap = false;

    private _debugMode = 0;

    private _shadersLoaded = false;
    private _breakShaderLoadedCheck = false;
    private _vertexPullingMetadata: Map<string, IVertexPullingMetadata> | null = null;

    /**
     * @internal
     * This is reserved for the inspector.
     * Defines the material debug mode.
     * It helps seeing only some components of the material while troubleshooting.
     */
    @expandToProperty("_markAllSubMeshesAsMiscDirty")
    public accessor debugMode = 0;

    /**
     * @internal
     * This is reserved for the inspector.
     * Specify from where on screen the debug mode should start.
     * The value goes from -1 (full screen) to 1 (not visible)
     * It helps with side by side comparison against the final render
     * This defaults to -1
     */
    public debugLimit = -1;

    /**
     * @internal
     * This is reserved for the inspector.
     * As the default viewing range might not be enough (if the ambient is really small for instance)
     * You can use the factor to better multiply the final value.
     */
    public debugFactor = 1;

    /**
     * Defines additional PrePass parameters for the material.
     */
    public readonly prePassConfiguration: PrePassConfiguration;

    protected _cacheHasRenderTargetTextures = false;

    /**
     * Instantiates a new OpenPBRMaterial instance.
     *
     * @param name The material name
     * @param scene The scene the material will be use in.
     * @param forceGLSL Use the GLSL code generation for the shader (even on WebGPU). Default is false
     */
    constructor(name: string, scene?: Scene, forceGLSL = false) {
        super(name, scene, undefined, forceGLSL || OpenPBRMaterial.ForceGLSL);
        // TODO: Check if we're running WebGL 2.0 or above
        if (this.getScene() && !this.getScene()?.getEngine().isWebGPU && (this.getScene().getEngine() as Engine).webGLVersion < 2) {
            Logger.Error("OpenPBRMaterial: WebGL 2.0 or above is required for this material.");
        }

        if (!OpenPBRMaterial._noiseTextures[this.getScene().uniqueId]) {
            OpenPBRMaterial._noiseTextures[this.getScene().uniqueId] = new Texture(
                Tools.GetAssetUrl("https://assets.babylonjs.com/core/blue_noise/blue_noise_rgb.png"),
                this.getScene(),
                false,
                true,
                Constants.TEXTURE_NEAREST_SAMPLINGMODE
            );
            this.getScene().onDisposeObservable.addOnce(() => {
                OpenPBRMaterial._noiseTextures[this.getScene().uniqueId]?.dispose();
                delete OpenPBRMaterial._noiseTextures[this.getScene().uniqueId];
            });
        }

        // Setup the default processing configuration to the scene.
        this._attachImageProcessingConfiguration(null);

        this.getRenderTargetTextures = (): SmartArray<RenderTargetTexture> => {
            this._renderTargets.reset();

            if (MaterialFlags.ReflectionTextureEnabled && this._radianceTexture && this._radianceTexture.isRenderTarget) {
                this._renderTargets.push(<RenderTargetTexture>this._radianceTexture);
            }

            if (MaterialFlags.RefractionTextureEnabled && this._backgroundRefractionTexture && this._backgroundRefractionTexture.isRenderTarget) {
                this._renderTargets.push(<RenderTargetTexture>this._backgroundRefractionTexture);
            }

            this._eventInfo.renderTargets = this._renderTargets;
            this._callbackPluginEventFillRenderTargetTextures(this._eventInfo);

            return this._renderTargets;
        };

        this._environmentBRDFTexture = GetOpenPBREnvironmentBRDFTexture(this.getScene());
        this._environmentFuzzBRDFTexture = GetEnvironmentFuzzBRDFTexture(this.getScene());
        this.prePassConfiguration = new PrePassConfiguration();

        // Build the internal property list that can be used to generate and update the uniform buffer
        this._propertyList = {};
        for (const key of Object.getOwnPropertyNames(this)) {
            const value = (this as any)[key];
            if (value instanceof Property) {
                this._propertyList[key] = value;
            }
        }
        // Build the internal uniforms list that is used for combining and updating
        // property values in the uniform buffer
        const propertyKeys = Object.keys(this._propertyList);
        propertyKeys.forEach((key) => {
            const prop = this._propertyList[key];
            let uniform = this._uniformsList[prop.targetUniformName];
            if (!uniform) {
                uniform = new Uniform(prop.targetUniformName, prop.targetUniformComponentNum);
                uniform.requiredDefine = prop.requiredDefine;
                this._uniformsList[prop.targetUniformName] = uniform;
            } else if (uniform.numComponents !== prop.targetUniformComponentNum) {
                Logger.Error(`Uniform ${prop.targetUniformName} already exists of size ${uniform.numComponents}, but trying to set it to ${prop.targetUniformComponentNum}.`);
            } else if (uniform.requiredDefine !== prop.requiredDefine) {
                // Properties packed into the same uniform must share the same gating
                // define, otherwise we cannot safely skip the per-frame UBO update.
                uniform.requiredDefine = undefined;
            }
            if (uniform.firstLinkedKey === "") {
                uniform.firstLinkedKey = prop.name;
            }
            uniform.linkedProperties[prop.name] = prop;
        });
        this._uniformsArray = Object.values(this._uniformsList);

        // Build the internal list of samplers
        this._samplersList = {};
        for (const key of Object.getOwnPropertyNames(this)) {
            const value = (this as any)[key];
            if (value instanceof Sampler) {
                this._samplersList[key] = value;
            }
        }

        // For each sampler in _samplersList, add defines to be added to OpenPBRMaterialDefines
        for (const samplerKey in this._samplersList) {
            const sampler = this._samplersList[samplerKey];
            const defineName = sampler.textureDefine;
            this._samplerDefines[defineName] = { type: "boolean", default: false };
            this._samplerDefines[defineName + "DIRECTUV"] = { type: "number", default: 0 };
            this._samplerDefines[defineName + "_GAMMA"] = { type: "boolean", default: false };
        }

        // Arg. Why do I have to add these references to get rid of the linting errors?
        this._baseWeight;
        this._baseWeightTexture;
        this._baseColor;
        this._baseColorTexture;
        this._baseDiffuseRoughness;
        this._baseDiffuseRoughnessTexture;
        this._baseMetalness;
        this._baseMetalnessTexture;
        this._specularWeight;
        this._specularWeightTexture;
        this._specularColor;
        this._specularColorTexture;
        this._specularRoughness;
        this._specularIor;
        this._specularRoughnessTexture;
        this._specularRoughnessAnisotropy;
        this._specularRoughnessAnisotropyTexture;
        this._transmissionWeight;
        this._transmissionWeightTexture;
        this._transmissionColor;
        this._transmissionColorTexture;
        this._transmissionDepth;
        this._transmissionDepthTexture;
        this._transmissionScatter;
        this._transmissionScatterTexture;
        this._transmissionScatterAnisotropy;
        this._transmissionDispersionScale;
        this._transmissionDispersionScaleTexture;
        this._transmissionDispersionAbbeNumber;
        this._subsurfaceWeight;
        this._subsurfaceWeightTexture;
        this._subsurfaceColor;
        this._subsurfaceColorTexture;
        this._subsurfaceRadius;
        this._subsurfaceRadiusScale;
        this._subsurfaceRadiusScaleTexture;
        this._subsurfaceScatterAnisotropy;
        this._coatWeight;
        this._coatWeightTexture;
        this._coatColor;
        this._coatColorTexture;
        this._coatRoughness;
        this._coatRoughnessTexture;
        this._coatRoughnessAnisotropy;
        this._coatRoughnessAnisotropyTexture;
        this._coatIor;
        this._coatDarkening;
        this._coatDarkeningTexture;
        this._fuzzWeight;
        this._fuzzWeightTexture;
        this._fuzzColor;
        this._fuzzColorTexture;
        this._fuzzRoughness;
        this._fuzzRoughnessTexture;
        this._geometryThinWalled;
        this._geometryNormalTexture;
        this._geometryTangent;
        this._geometryTangentTexture;
        this._geometryCoatNormalTexture;
        this._geometryCoatTangent;
        this._geometryCoatTangentTexture;
        this._geometryOpacity;
        this._geometryOpacityTexture;
        this._geometryThickness;
        this._geometryThicknessTexture;
        this._thinFilmWeight;
        this._thinFilmWeightTexture;
        this._thinFilmThickness;
        this._thinFilmThicknessMin;
        this._thinFilmThicknessTexture;
        this._thinFilmIor;
        this._emissionLuminance;
        this._emissionColor;
        this._emissionColorTexture;
        this._ambientOcclusionTexture;
    }

    /**
     * Gets a boolean indicating that current material needs to register RTT
     */
    public override get hasRenderTargetTextures(): boolean {
        if (MaterialFlags.ReflectionTextureEnabled && this._radianceTexture && this._radianceTexture.isRenderTarget) {
            return true;
        }

        if (MaterialFlags.RefractionTextureEnabled && this._backgroundRefractionTexture && this._backgroundRefractionTexture.isRenderTarget) {
            return true;
        }

        return this._cacheHasRenderTargetTextures;
    }

    /**
     * Can this material render to prepass
     */
    public override get isPrePassCapable(): boolean {
        return !this.disableDepthWrite;
    }

    /**
     * @returns the name of the material class.
     */
    public override getClassName(): string {
        return "OpenPBRMaterial";
    }

    protected override _transparencyMode: number = Material.MATERIAL_OPAQUE;

    @serialize()
    public override get transparencyMode(): number {
        return this._transparencyMode;
    }

    public override set transparencyMode(value: number) {
        if (this._transparencyMode === value) {
            return;
        }

        this._transparencyMode = value;

        this._markAllSubMeshesAsTexturesAndMiscDirty();
    }

    /**
     * @returns whether or not the alpha value of the albedo texture should be used for alpha blending.
     */
    protected _shouldUseAlphaFromBaseColorTexture(): boolean {
        return this._hasAlphaChannel() && this._transparencyMode !== Material.MATERIAL_OPAQUE && !this.geometryOpacityTexture;
    }

    /**
     * @returns whether or not there is a usable alpha channel for transparency.
     */
    protected _hasAlphaChannel(): boolean {
        return (this.baseColorTexture != null && this.baseColorTexture.hasAlpha && this._useAlphaFromBaseColorTexture) || this.geometryOpacityTexture != null;
    }

    /**
     * @returns true when the material needs alpha blending — i.e. when geometry_opacity < 1
     * or a geometry opacity texture is present.
     */
    public override needAlphaBlending(): boolean {
        return this.geometryOpacity < 1.0 || this.geometryOpacityTexture != null;
    }

    /**
     * Specifies if the mesh will require alpha blending.
     * Overridden to check geometry_opacity before the _hasTransparencyMode short-circuit in the
     * base class, which would otherwise always return false when _transparencyMode is MATERIAL_OPAQUE.
     * @param mesh The mesh to check
     * @returns true if alpha blending is needed for the mesh
     */
    public override needAlphaBlendingForMesh(mesh: AbstractMesh): boolean {
        return this.geometryOpacity < 1.0 || this.geometryOpacityTexture != null || super.needAlphaBlendingForMesh(mesh);
    }

    /**
     * Makes a duplicate of the current material.
     * @param name - name to use for the new material.
     * @param cloneTexturesOnlyOnce - if a texture is used in more than one channel (e.g baseColor and opacity), only clone it once and reuse it on the other channels. Default false.
     * @param rootUrl defines the root URL to use to load textures
     * @returns cloned material instance
     */
    public override clone(name: string, cloneTexturesOnlyOnce: boolean = true, rootUrl = ""): OpenPBRMaterial {
        const clone = SerializationHelper.Clone(() => new OpenPBRMaterial(name, this.getScene()), this, { cloneTexturesOnlyOnce });

        clone.id = name;
        clone.name = name;

        this.stencil.copyTo(clone.stencil);

        this._clonePlugins(clone, rootUrl);

        return clone;
    }

    /**
     * Serializes this PBR Material.
     * @returns - An object with the serialized material.
     */
    public override serialize(): any {
        const serializationObject = super.serialize();
        serializationObject.customType = "BABYLON.OpenPBRMaterial";

        return serializationObject;
    }

    // Statics
    /**
     * Parses a PBR Material from a serialized object.
     * @param source - Serialized object.
     * @param scene - BJS scene instance.
     * @param rootUrl - url for the scene object
     * @returns - OpenPBRMaterial
     */
    public static override Parse(source: any, scene: Scene, rootUrl: string): OpenPBRMaterial {
        const material = SerializationHelper.Parse(() => new OpenPBRMaterial(source.name, scene), source, scene, rootUrl);

        if (source.stencil) {
            material.stencil.parse(source.stencil, scene, rootUrl);
        }

        Material._ParsePlugins(source, material, scene, rootUrl);

        return material;
    }

    /**
     * Force shader compilation
     * @param mesh - Define the mesh we want to force the compilation for
     * @param onCompiled - Define a callback triggered when the compilation completes
     * @param options - Define the options used to create the compilation
     */
    public override forceCompilation(mesh: AbstractMesh, onCompiled?: (material: Material) => void, options?: Partial<IMaterialCompilationOptions>): void {
        const localOptions = {
            clipPlane: false,
            useInstances: false,
            ...options,
        };

        if (!this._uniformBufferLayoutBuilt) {
            this.buildUniformLayout();
        }

        this._callbackPluginEventGeneric(MaterialPluginEvent.GetDefineNames, this._eventInfo);
        const checkReady = () => {
            if (this._breakShaderLoadedCheck) {
                return;
            }

            const defines = new OpenPBRMaterialDefines({
                ...(this._eventInfo.defineNames || {}),
                ...(this._samplerDefines || {}),
            });
            const effect = this._prepareEffect(mesh, mesh, defines, undefined, undefined, localOptions.useInstances, localOptions.clipPlane)!;
            if (this._onEffectCreatedObservable) {
                onCreatedEffectParameters.effect = effect;
                onCreatedEffectParameters.subMesh = null;
                this._onEffectCreatedObservable.notifyObservers(onCreatedEffectParameters);
            }
            if (effect.isReady()) {
                if (onCompiled) {
                    onCompiled(this);
                }
            } else {
                effect.onCompileObservable.add(() => {
                    if (onCompiled) {
                        onCompiled(this);
                    }
                });
            }
        };
        checkReady();
    }

    /**
     * Specifies that the submesh is ready to be used.
     * @param mesh - BJS mesh.
     * @param subMesh - A submesh of the BJS mesh.  Used to check if it is ready.
     * @param useInstances - Specifies that instances should be used.
     * @returns - boolean indicating that the submesh is ready or not.
     */
    public override isReadyForSubMesh(mesh: AbstractMesh, subMesh: SubMesh, useInstances?: boolean): boolean {
        if (!this._uniformBufferLayoutBuilt) {
            this.buildUniformLayout();
        }

        const drawWrapper = subMesh._drawWrapper;

        if (drawWrapper.effect && this.isFrozen) {
            if (drawWrapper._wasPreviouslyReady && drawWrapper._wasPreviouslyUsingInstances === useInstances) {
                return true;
            }
        }

        if (!subMesh.materialDefines) {
            this._callbackPluginEventGeneric(MaterialPluginEvent.GetDefineNames, this._eventInfo);
            subMesh.materialDefines = new OpenPBRMaterialDefines({
                ...(this._eventInfo.defineNames || {}),
                ...(this._samplerDefines || {}),
            });
        }

        const defines = <OpenPBRMaterialDefines>subMesh.materialDefines;
        if (this._isReadyForSubMesh(subMesh)) {
            return true;
        }

        const scene = this.getScene();
        const engine = scene.getEngine();

        if (defines._areTexturesDirty) {
            this._eventInfo.hasRenderTargetTextures = false;
            this._callbackPluginEventHasRenderTargetTextures(this._eventInfo);
            this._cacheHasRenderTargetTextures = this._eventInfo.hasRenderTargetTextures;
            if (scene.texturesEnabled) {
                // Loop through samplers, check MaterialFlag and whether the texture is ready or not.
                for (const key in this._samplersList) {
                    const sampler = this._samplersList[key];
                    if (sampler.value) {
                        if (!sampler.value.isReadyOrNotBlocking()) {
                            return false;
                        }
                    }
                }

                const radianceTexture = this._getRadianceTexture();
                if (radianceTexture && MaterialFlags.ReflectionTextureEnabled) {
                    if (!radianceTexture.isReadyOrNotBlocking()) {
                        return false;
                    }
                    if (radianceTexture.irradianceTexture) {
                        if (!radianceTexture.irradianceTexture.isReadyOrNotBlocking()) {
                            return false;
                        }
                    } else {
                        // Not ready until spherical are ready too.
                        if (!radianceTexture.sphericalPolynomial && radianceTexture.getInternalTexture()?._sphericalPolynomialPromise) {
                            return false;
                        }
                    }
                }

                if (this._environmentBRDFTexture && MaterialFlags.ReflectionTextureEnabled) {
                    // This is blocking.
                    if (!this._environmentBRDFTexture.isReady()) {
                        return false;
                    }
                }

                if (this._environmentFuzzBRDFTexture && MaterialFlags.ReflectionTextureEnabled) {
                    // This is blocking.
                    if (!this._environmentFuzzBRDFTexture.isReady()) {
                        return false;
                    }
                }

                if (this._backgroundRefractionTexture && MaterialFlags.RefractionTextureEnabled) {
                    if (!this._backgroundRefractionTexture.isReadyOrNotBlocking()) {
                        return false;
                    }
                }

                if (OpenPBRMaterial._noiseTextures[scene.uniqueId]) {
                    if (!OpenPBRMaterial._noiseTextures[scene.uniqueId].isReady()) {
                        return false;
                    }
                }

                // When both SSS textures are assigned they will be used for screen-space subsurface
                // scattering. Block readiness until both underlying textures are loaded so that
                // scene.onReadyObservable never fires with missing SSS data.
                if (this._sssIrradianceTexture && this._sssDepthTexture) {
                    if (!this._sssIrradianceTexture.isReady()) {
                        return false;
                    }
                    if (!this._sssDepthTexture.isReady()) {
                        return false;
                    }
                }
            }
        }

        this._eventInfo.isReadyForSubMesh = true;
        this._eventInfo.defines = defines;
        this._eventInfo.subMesh = subMesh;
        this._callbackPluginEventIsReadyForSubMesh(this._eventInfo);

        if (!this._eventInfo.isReadyForSubMesh) {
            return false;
        }

        if (defines._areImageProcessingDirty && this._imageProcessingConfiguration) {
            if (!this._imageProcessingConfiguration.isReady()) {
                return false;
            }
        }

        // Check if Area Lights have LTC texture.
        if (defines["AREALIGHTUSED"]) {
            for (let index = 0; index < mesh.lightSources.length; index++) {
                if (!mesh.lightSources[index]._isReady()) {
                    return false;
                }
            }
        }

        if (!engine.getCaps().standardDerivatives && !mesh.isVerticesDataPresent(VertexBuffer.NormalKind)) {
            mesh.createNormals(true);
            Logger.Warn("OpenPBRMaterial: Normals have been created for the mesh: " + mesh.name);
        }

        if (!AreLightsTexturesReady(scene, mesh, this._maxSimultaneousLights, this._disableLighting)) {
            return false;
        }

        const previousEffect = subMesh.effect;
        const lightDisposed = defines._areLightsDisposed;
        const effect = this._prepareEffect(mesh, subMesh.getRenderingMesh(), defines, this.onCompiled, this.onError, useInstances, null);

        let forceWasNotReadyPreviously = false;

        if (effect) {
            if (this._onEffectCreatedObservable) {
                onCreatedEffectParameters.effect = effect;
                onCreatedEffectParameters.subMesh = subMesh;
                this._onEffectCreatedObservable.notifyObservers(onCreatedEffectParameters);
            }

            // Use previous effect while new one is compiling
            if (this.allowShaderHotSwapping && previousEffect && !effect.isReady()) {
                defines.markAsUnprocessed();

                forceWasNotReadyPreviously = this.isFrozen;

                if (lightDisposed) {
                    // re register in case it takes more than one frame.
                    defines._areLightsDisposed = true;
                    return false;
                }
            } else {
                scene.resetCachedMaterial();
                subMesh.setEffect(effect, defines, this._materialContext);
            }
        }

        if (!subMesh.effect || !subMesh.effect.isReady()) {
            return false;
        }

        defines._renderId = scene.getRenderId();
        drawWrapper._wasPreviouslyReady = forceWasNotReadyPreviously ? false : true;
        drawWrapper._wasPreviouslyUsingInstances = !!useInstances;

        this._checkScenePerformancePriority();

        return true;
    }

    /**
     * Initializes the uniform buffer layout for the shader.
     */
    public override buildUniformLayout(): void {
        // Order is important !
        const ubo = this._uniformBuffer;
        ubo.addUniform("vTangentSpaceParams", 2);
        ubo.addUniform("vLightingIntensity", 4);

        ubo.addUniform("pointSize", 1);

        ubo.addUniform("vDebugMode", 2);
        ubo.addUniform("renderTargetSize", 2);
        ubo.addUniform("cameraInfo", 4);
        ubo.addUniform("vTextureRepetitionHexTilingParams", 4);
        ubo.addUniform("backgroundRefractionMatrix", 16);
        ubo.addUniform("vBackgroundRefractionInfos", 3);
        PrepareUniformLayoutForIBL(ubo, true, true, true, true, true);

        Object.values(this._uniformsList).forEach((uniform) => {
            ubo.addUniform(uniform.name, uniform.numComponents);
        });

        Object.values(this._samplersList).forEach((sampler) => {
            ubo.addUniform(sampler.samplerInfoName, 2);
            ubo.addUniform(sampler.samplerMatrixName, 16);
        });

        super.buildUniformLayout();
    }

    /**
     * Binds the material data (this function is called even if mustRebind() returns false)
     * @param uniformBuffer defines the Uniform buffer to fill in.
     * @param scene defines the scene the material belongs to.
     * @param engine defines the engine the material belongs to.
     * @param subMesh the submesh to bind data for
     */
    public bindPropertiesForSubMesh(uniformBuffer: UniformBuffer, scene: Scene, engine: Engine, subMesh: SubMesh): void {
        // If min/max thickness is 0, avoid decomposing to determine the scaled thickness (it's always zero).
        if (this.geometryThickness === 0.0) {
            uniformBuffer.updateFloat("vGeometryThickness", 0);
        } else {
            subMesh.getRenderingMesh().getWorldMatrix().decompose(TmpVectors.Vector3[0]);
            const thicknessScale = Math.max(Math.abs(TmpVectors.Vector3[0].x), Math.abs(TmpVectors.Vector3[0].y), Math.abs(TmpVectors.Vector3[0].z));
            uniformBuffer.updateFloat("vGeometryThickness", this.geometryThickness * thicknessScale);
        }
    }

    /**
     * Binds the submesh data.
     * @param world - The world matrix.
     * @param mesh - The BJS mesh.
     * @param subMesh - A submesh of the BJS mesh.
     */
    public override bindForSubMesh(world: Matrix, mesh: Mesh, subMesh: SubMesh): void {
        const scene = this.getScene();

        const defines = <OpenPBRMaterialDefines>subMesh.materialDefines;
        if (!defines) {
            return;
        }

        const effect = subMesh.effect;

        if (!effect) {
            return;
        }

        this._activeEffect = effect;

        // Matrices Mesh.
        mesh.getMeshUniformBuffer().bindToEffect(effect, "Mesh");
        mesh.transferToEffect(world);

        const engine = scene.getEngine();

        // Binding unconditionally
        this._uniformBuffer.bindToEffect(effect, "Material");

        this.prePassConfiguration.bindForSubMesh(this._activeEffect, scene, mesh, world, this.isFrozen);

        MaterialHelperGeometryRendering.Bind(engine.currentRenderPassId, this._activeEffect, mesh, world, this);

        const camera = scene.activeCamera;
        if (camera) {
            this._uniformBuffer.updateFloat4("cameraInfo", camera.minZ, camera.maxZ, 0, 0);
        } else {
            this._uniformBuffer.updateFloat4("cameraInfo", 0, 0, 0, 0);
        }

        const hexParams = this.textureRepetitionHexTilingParams;
        this._uniformBuffer.updateFloat4("vTextureRepetitionHexTilingParams", hexParams[0], hexParams[1], hexParams[2], hexParams[3]);

        this._eventInfo.subMesh = subMesh;
        this._callbackPluginEventHardBindForSubMesh(this._eventInfo);

        // Normal Matrix
        if (defines.OBJECTSPACE_NORMALMAP) {
            world.toNormalMatrix(this._normalMatrix);
            this.bindOnlyNormalMatrix(this._normalMatrix);
        }

        const mustRebind = this._mustRebind(scene, effect, subMesh, mesh.visibility);
        const needToAlwaysBindUniformBuffers = engine._features.needToAlwaysBindUniformBuffers;

        // Bones
        BindBonesParameters(mesh, this._activeEffect, this.prePassConfiguration);

        // Vertex pulling
        if (this._vertexPullingMetadata) {
            BindVertexPullingUniforms(this._activeEffect, this._vertexPullingMetadata);
        }

        const ubo = this._uniformBuffer;
        if (mustRebind) {
            this.bindViewProjection(effect);
            const radianceTexture: Nullable<BaseTexture> = this._getRadianceTexture();

            if (!ubo.useUbo || !this.isFrozen || !ubo.isSync || subMesh._drawWrapper._forceRebindOnNextCall) {
                // Texture uniforms
                if (scene.texturesEnabled) {
                    // Loop through samplers and bind info and matrix for each texture.
                    for (const key in this._samplersList) {
                        const sampler = this._samplersList[key];
                        if (sampler.value) {
                            ubo.updateFloat2(sampler.samplerInfoName, sampler.value.coordinatesIndex, sampler.value.level);
                            BindTextureMatrix(sampler.value, ubo, sampler.samplerPrefix);
                        }
                    }

                    if (this.geometryNormalTexture || this.geometryCoatNormalTexture) {
                        if (scene._mirroredCameraPosition) {
                            ubo.updateFloat2("vTangentSpaceParams", this._invertNormalMapX ? 1.0 : -1.0, this._invertNormalMapY ? 1.0 : -1.0);
                        } else {
                            ubo.updateFloat2("vTangentSpaceParams", this._invertNormalMapX ? -1.0 : 1.0, this._invertNormalMapY ? -1.0 : 1.0);
                        }
                    }

                    BindIBLParameters(scene, defines, ubo, Color3.White(), radianceTexture, this.realTimeFiltering, true, true, true, true, true);
                }

                // Point size
                if (this.pointsCloud) {
                    ubo.updateFloat("pointSize", this.pointSize);
                }

                const uniformsArray = this._uniformsArray;
                for (let i = 0, len = uniformsArray.length; i < len; i++) {
                    const uniform = uniformsArray[i];
                    // Skip uniforms whose define is currently inactive. The shader only
                    // reads them inside the same #ifdef block, so the UBO bytes can stay
                    // stale. The full update will happen on the next bind once the
                    // define becomes active again.
                    if (uniform.requiredDefine !== undefined && !defines[uniform.requiredDefine]) {
                        continue;
                    }
                    // If the property actually defines a uniform, update it.
                    if (uniform.numComponents === 4) {
                        uniform.populateVectorFromLinkedProperties(TmpVectors.Vector4[0]);
                        ubo.updateVector4(uniform.name, TmpVectors.Vector4[0]);
                    } else if (uniform.numComponents === 3) {
                        uniform.populateVectorFromLinkedProperties(TmpVectors.Vector3[0]);
                        ubo.updateVector3(uniform.name, TmpVectors.Vector3[0]);
                    } else if (uniform.numComponents === 2) {
                        uniform.populateVectorFromLinkedProperties(TmpVectors.Vector2[0]);
                        ubo.updateFloat2(uniform.name, TmpVectors.Vector2[0].x, TmpVectors.Vector2[0].y);
                    } else if (uniform.numComponents === 1) {
                        ubo.updateFloat(uniform.name, uniform.linkedProperties[uniform.firstLinkedKey].value as number);
                    }
                }

                // Misc
                this._lightingInfos.x = this.directIntensity;
                this._lightingInfos.y = this.emissionLuminance;
                this._lightingInfos.z = this.environmentIntensity * scene.environmentIntensity;
                this._lightingInfos.w = 1.0; // This is used to be _specularIntensity.

                ubo.updateVector4("vLightingIntensity", this._lightingInfos);

                ubo.updateFloat2("vDebugMode", this.debugLimit, this.debugFactor);
            }

            // Textures
            if (scene.texturesEnabled) {
                // Loop through samplers and set textures
                for (const key in this._samplersList) {
                    const sampler = this._samplersList[key];
                    if (sampler.value) {
                        ubo.setTexture(sampler.samplerName, sampler.value);
                    }
                }

                BindIBLSamplers(scene, defines, ubo, radianceTexture, this.realTimeFiltering);

                if (defines.ENVIRONMENTBRDF) {
                    ubo.setTexture("environmentBrdfSampler", this._environmentBRDFTexture);
                }

                if (defines.FUZZENVIRONMENTBRDF) {
                    ubo.setTexture("environmentFuzzBrdfSampler", this._environmentFuzzBRDFTexture);
                }

                if (defines.REFRACTED_BACKGROUND) {
                    ubo.setTexture("backgroundRefractionSampler", this._backgroundRefractionTexture);
                    ubo.updateMatrix("backgroundRefractionMatrix", this._backgroundRefractionTexture!.getReflectionTextureMatrix());
                    TmpVectors.Vector3[1].set(Math.log2(this._backgroundRefractionTexture!.getSize().width), 0, 0);
                    ubo.updateVector3("vBackgroundRefractionInfos", TmpVectors.Vector3[1]);
                }

                if (defines.ANISOTROPIC || defines.FUZZ || defines.REFRACTED_BACKGROUND || defines.USE_IRRADIANCE_TEXTURE_FOR_SCATTERING) {
                    ubo.setTexture("blueNoiseSampler", OpenPBRMaterial._noiseTextures[this.getScene().uniqueId]);
                }

                if (defines.USE_IRRADIANCE_TEXTURE_FOR_SCATTERING) {
                    if (this.sssIrradianceTexture && this.sssDepthTexture) {
                        const renderTargetWidth = this.sssIrradianceTexture.getSize().width;
                        const renderTargetHeight = this.sssIrradianceTexture.getSize().height;
                        ubo.setTexture("sceneIrradianceSampler", this.sssIrradianceTexture);
                        ubo.setTexture("sceneDepthSampler", this.sssDepthTexture);
                        ubo.updateFloat2("renderTargetSize", renderTargetWidth, renderTargetHeight);
                    }
                }
            }

            // OIT with depth peeling
            if (this.getScene().useOrderIndependentTransparency && this.needAlphaBlendingForMesh(mesh)) {
                this.getScene().depthPeelingRenderer!.bind(effect);
            }

            this._eventInfo.subMesh = subMesh;
            this._callbackPluginEventBindForSubMesh(this._eventInfo);

            // Clip plane
            BindClipPlane(this._activeEffect, this, scene);

            this.bindEyePosition(effect);
        } else if (needToAlwaysBindUniformBuffers) {
            this._needToBindSceneUbo = true;
        }

        this.bindPropertiesForSubMesh(this._uniformBuffer, scene, scene.getEngine() as Engine, subMesh);

        // Lights
        if ((mustRebind || !this.isFrozen || needToAlwaysBindUniformBuffers) && scene.lightsEnabled && !this._disableLighting) {
            BindLights(scene, mesh, this._activeEffect, defines, this._maxSimultaneousLights);
        }

        if (mustRebind || !this.isFrozen) {
            // View
            this.bindView(effect);

            // Fog
            BindFogParameters(scene, mesh, this._activeEffect, true);

            // Morph targets
            if (defines.NUM_MORPH_INFLUENCERS) {
                BindMorphTargetParameters(mesh, this._activeEffect);
            }

            if (defines.BAKED_VERTEX_ANIMATION_TEXTURE) {
                mesh.bakedVertexAnimationManager?.bind(effect, defines.INSTANCES);
            }

            // image processing
            this._imageProcessingConfiguration.bind(this._activeEffect);

            // Log. depth
            BindLogDepth(defines, this._activeEffect, scene);
        }

        this._afterBind(mesh, this._activeEffect, subMesh);

        ubo.update();
    }

    /**
     * Returns the animatable textures.
     * If material have animatable metallic texture, then reflectivity texture will not be returned, even if it has animations.
     * @returns - Array of animatable textures.
     */
    public override getAnimatables(): IAnimatable[] {
        const results = super.getAnimatables();

        // Loop through samplers and push animated textures to list.
        for (const key in this._samplersList) {
            const sampler = this._samplersList[key];
            if (sampler.value && sampler.value.animations && sampler.value.animations.length > 0) {
                results.push(sampler.value);
            }
        }

        if (this._radianceTexture && this._radianceTexture.animations && this._radianceTexture.animations.length > 0) {
            results.push(this._radianceTexture);
        }

        return results;
    }

    /**
     * Returns an array of the actively used textures.
     * @returns - Array of BaseTextures
     */
    public override getActiveTextures(): BaseTexture[] {
        const activeTextures = super.getActiveTextures();

        // Loop through samplers and push active textures
        for (const key in this._samplersList) {
            const sampler = this._samplersList[key];
            if (sampler.value) {
                activeTextures.push(sampler.value);
            }
        }

        if (this._radianceTexture) {
            activeTextures.push(this._radianceTexture);
        }

        return activeTextures;
    }

    /**
     * Checks to see if a texture is used in the material.
     * @param texture - Base texture to use.
     * @returns - Boolean specifying if a texture is used in the material.
     */
    public override hasTexture(texture: BaseTexture): boolean {
        if (super.hasTexture(texture)) {
            return true;
        }

        // Loop through samplers and check each texture for equality
        for (const key in this._samplersList) {
            const sampler = this._samplersList[key];
            if (sampler.value === texture) {
                return true;
            }
        }

        if (this._radianceTexture === texture) {
            return true;
        }

        return false;
    }

    /**
     * Sets the required values to the prepass renderer.
     * It can't be sets when subsurface scattering of this material is disabled.
     * When scene have ability to enable subsurface prepass effect, it will enable.
     * @returns - If prepass is enabled or not.
     */
    public override setPrePassRenderer(): boolean {
        return false;
    }

    /**
     * Disposes the resources of the material.
     * @param forceDisposeEffect - Forces the disposal of effects.
     * @param forceDisposeTextures - Forces the disposal of all textures.
     */
    public override dispose(forceDisposeEffect?: boolean, forceDisposeTextures?: boolean): void {
        this._breakShaderLoadedCheck = true;
        if (forceDisposeTextures) {
            if (this._environmentBRDFTexture && (this.getScene() as any).openPBREnvironmentBRDFTexture !== this._environmentBRDFTexture) {
                this._environmentBRDFTexture.dispose();
            }
            if (this._environmentFuzzBRDFTexture && this.getScene().environmentFuzzBRDFTexture !== this._environmentFuzzBRDFTexture) {
                this._environmentFuzzBRDFTexture.dispose();
            }
            // The refraction texture will be cleaned up by the transmission helper.
            this._backgroundRefractionTexture = null;

            // Loop through samplers and dispose the textures
            for (const key in this._samplersList) {
                const sampler = this._samplersList[key];
                sampler.value?.dispose();
            }

            this._radianceTexture?.dispose();
        }

        this._renderTargets.dispose();

        if (this._imageProcessingConfiguration && this._imageProcessingObserver) {
            this._imageProcessingConfiguration.onUpdateParameters.remove(this._imageProcessingObserver);
        }

        super.dispose(forceDisposeEffect, forceDisposeTextures);
    }

    /**
     * Returns the texture used for reflections.
     * @returns - Reflection texture if present.  Otherwise, returns the environment texture.
     */
    private _getRadianceTexture(): Nullable<BaseTexture> {
        if (this._radianceTexture) {
            return this._radianceTexture;
        }

        return this.getScene().environmentTexture;
    }

    private _prepareEffect(
        mesh: AbstractMesh,
        renderingMesh: AbstractMesh,
        defines: OpenPBRMaterialDefines,
        onCompiled: Nullable<(effect: Effect) => void> = null,
        onError: Nullable<(effect: Effect, errors: string) => void> = null,
        useInstances: Nullable<boolean> = null,
        useClipPlane: Nullable<boolean> = null
    ): Nullable<Effect> {
        this._prepareDefines(mesh, renderingMesh, defines, useInstances, useClipPlane);

        if (!defines.isDirty) {
            return null;
        }

        defines.markAsProcessed();

        const scene = this.getScene();
        const engine = scene.getEngine();

        // Fallbacks
        const fallbacks = new EffectFallbacks();
        let fallbackRank = 0;
        if (defines.USESPHERICALINVERTEX) {
            fallbacks.addFallback(fallbackRank++, "USESPHERICALINVERTEX");
        }

        if (defines.FOG) {
            fallbacks.addFallback(fallbackRank, "FOG");
        }
        if (defines.SPECULARAA) {
            fallbacks.addFallback(fallbackRank, "SPECULARAA");
        }
        if (defines.POINTSIZE) {
            fallbacks.addFallback(fallbackRank, "POINTSIZE");
        }
        if (defines.LOGARITHMICDEPTH) {
            fallbacks.addFallback(fallbackRank, "LOGARITHMICDEPTH");
        }
        if (defines.PARALLAX) {
            fallbacks.addFallback(fallbackRank, "PARALLAX");
        }
        if (defines.PARALLAX_RHS) {
            fallbacks.addFallback(fallbackRank, "PARALLAX_RHS");
        }
        if (defines.PARALLAXOCCLUSION) {
            fallbacks.addFallback(fallbackRank++, "PARALLAXOCCLUSION");
        }

        if (defines.ENVIRONMENTBRDF) {
            fallbacks.addFallback(fallbackRank++, "ENVIRONMENTBRDF");
        }

        if (defines.TANGENT) {
            fallbacks.addFallback(fallbackRank++, "TANGENT");
        }

        fallbackRank = HandleFallbacksForShadows(defines, fallbacks, this._maxSimultaneousLights, fallbackRank);

        if (defines.SPECULARTERM) {
            fallbacks.addFallback(fallbackRank++, "SPECULARTERM");
        }

        if (defines.USESPHERICALFROMREFLECTIONMAP) {
            fallbacks.addFallback(fallbackRank++, "USESPHERICALFROMREFLECTIONMAP");
        }

        if (defines.USEIRRADIANCEMAP) {
            fallbacks.addFallback(fallbackRank++, "USEIRRADIANCEMAP");
        }

        if (defines.NORMAL) {
            fallbacks.addFallback(fallbackRank++, "NORMAL");
        }

        if (defines.VERTEXCOLOR) {
            fallbacks.addFallback(fallbackRank++, "VERTEXCOLOR");
        }

        if (defines.MORPHTARGETS) {
            fallbacks.addFallback(fallbackRank++, "MORPHTARGETS");
        }

        if (defines.MULTIVIEW) {
            fallbacks.addFallback(0, "MULTIVIEW");
        }

        //Attributes
        const attribs = [VertexBuffer.PositionKind];

        if (defines.NORMAL) {
            attribs.push(VertexBuffer.NormalKind);
        }

        if (defines.TANGENT) {
            attribs.push(VertexBuffer.TangentKind);
        }

        for (let i = 1; i <= Constants.MAX_SUPPORTED_UV_SETS; ++i) {
            if (defines["UV" + i]) {
                attribs.push(`uv${i === 1 ? "" : i}`);
            }
        }

        if (defines.VERTEXCOLOR) {
            attribs.push(VertexBuffer.ColorKind);
        }

        PrepareAttributesForBones(attribs, mesh, defines, fallbacks);
        PrepareAttributesForInstances(attribs, defines);
        PrepareAttributesForMorphTargets(attribs, mesh, defines);
        PrepareAttributesForBakedVertexAnimation(attribs, mesh, defines);

        let shaderName = "openpbr";

        const uniforms = [
            "world",
            "view",
            "viewProjection",
            "projection",
            "vEyePosition",
            "inverseProjection",
            "renderTargetSize",
            "vLightsType",
            "visibility",
            "vFogInfos",
            "vFogColor",
            "pointSize",
            "mBones",
            "normalMatrix",
            "vLightingIntensity",
            "logarithmicDepthConstant",
            "vTangentSpaceParams",
            "boneTextureInfo",
            "vDebugMode",
            "morphTargetTextureInfo",
            "morphTargetTextureIndices",
            "cameraInfo",
            "vTextureRepetitionHexTilingParams",
            "backgroundRefractionMatrix",
            "vBackgroundRefractionInfos",
        ];

        for (const uniformName in this._uniformsList) {
            uniforms.push(uniformName);
        }

        const samplers = ["environmentBrdfSampler", "boneSampler", "morphTargets", "oitDepthSampler", "oitFrontColorSampler", "areaLightsLTC1Sampler", "areaLightsLTC2Sampler"];

        if (defines.FUZZENVIRONMENTBRDF) {
            samplers.push("environmentFuzzBrdfSampler");
        }

        if (defines.REFRACTED_BACKGROUND) {
            samplers.push("backgroundRefractionSampler");
        }

        if (defines.ANISOTROPIC || defines.FUZZ || defines.REFRACTED_BACKGROUND || defines.USE_IRRADIANCE_TEXTURE_FOR_SCATTERING) {
            samplers.push("blueNoiseSampler");
        }

        if (defines.USE_IRRADIANCE_TEXTURE_FOR_SCATTERING) {
            samplers.push("sceneIrradianceSampler");
            samplers.push("sceneDepthSampler");
        }

        for (const key in this._samplersList) {
            const sampler = this._samplersList[key];
            samplers.push(sampler.samplerName);

            // Push uniforms for texture infos and matrix
            uniforms.push(sampler.samplerInfoName);
            uniforms.push(sampler.samplerMatrixName);
        }

        PrepareUniformsAndSamplersForIBL(uniforms, samplers, true);

        const uniformBuffers = ["Material", "Scene", "Mesh"];

        const indexParameters = { maxSimultaneousLights: this._maxSimultaneousLights, maxSimultaneousMorphTargets: defines.NUM_MORPH_INFLUENCERS };

        this._eventInfo.fallbacks = fallbacks;
        this._eventInfo.fallbackRank = fallbackRank;
        this._eventInfo.defines = defines;
        this._eventInfo.uniforms = uniforms;
        this._eventInfo.attributes = attribs;
        this._eventInfo.samplers = samplers;
        this._eventInfo.uniformBuffersNames = uniformBuffers;
        this._eventInfo.customCode = undefined;
        this._eventInfo.mesh = mesh;
        this._eventInfo.indexParameters = indexParameters;
        this._callbackPluginEventGeneric(MaterialPluginEvent.PrepareEffect, this._eventInfo);

        MaterialHelperGeometryRendering.AddUniformsAndSamplers(uniforms, samplers);

        PrePassConfiguration.AddUniforms(uniforms);
        PrePassConfiguration.AddSamplers(samplers);
        AddClipPlaneUniforms(uniforms);

        // Vertex pulling metadata uniforms
        if (this._useVertexPulling) {
            const geometry = (renderingMesh as Mesh)?.geometry;
            if (geometry) {
                this._vertexPullingMetadata = PrepareVertexPullingUniforms(geometry);
                if (this._vertexPullingMetadata) {
                    this._vertexPullingMetadata.forEach((_, attribute) => {
                        uniforms.push(`vp_${attribute}_info`);
                    });
                }
            }
        } else {
            this._vertexPullingMetadata = null;
        }

        if (ImageProcessingConfiguration) {
            ImageProcessingConfiguration.PrepareUniforms(uniforms, defines);
            ImageProcessingConfiguration.PrepareSamplers(samplers, defines);
        }

        PrepareUniformsAndSamplersList(<IEffectCreationOptions>{
            uniformsNames: uniforms,
            uniformBuffersNames: uniformBuffers,
            samplers: samplers,
            defines: defines,
            maxSimultaneousLights: this._maxSimultaneousLights,
            shaderLanguage: this._shaderLanguage,
        });

        const csnrOptions: ICustomShaderNameResolveOptions = {};

        if (this.customShaderNameResolve) {
            shaderName = this.customShaderNameResolve(shaderName, uniforms, uniformBuffers, samplers, defines, attribs, csnrOptions);
        }

        const join = defines.toString();
        const effect = engine.createEffect(
            shaderName,
            <IEffectCreationOptions>{
                attributes: attribs,
                uniformsNames: uniforms,
                uniformBuffersNames: uniformBuffers,
                samplers: samplers,
                defines: join,
                fallbacks: fallbacks,
                onCompiled: onCompiled,
                onError: onError,
                indexParameters,
                processFinalCode: csnrOptions.processFinalCode,
                processCodeAfterIncludes: this._eventInfo.customCode,
                multiTarget: defines.PREPASS,
                shaderLanguage: this._shaderLanguage,
                extraInitializationsAsync: this._shadersLoaded
                    ? undefined
                    : async () => {
                          if (this.shaderLanguage === ShaderLanguage.WGSL) {
                              await Promise.all([import("../../ShadersWGSL/openpbr.vertex"), import("../../ShadersWGSL/openpbr.fragment")]);
                          } else {
                              await Promise.all([import("../../Shaders/openpbr.vertex"), import("../../Shaders/openpbr.fragment")]);
                          }

                          this._shadersLoaded = true;
                      },
            },
            engine
        );

        this._eventInfo.customCode = undefined;

        return effect;
    }

    private _prepareDefines(
        mesh: AbstractMesh,
        renderingMesh: AbstractMesh,
        defines: OpenPBRMaterialDefines,
        useInstances: Nullable<boolean> = null,
        useClipPlane: Nullable<boolean> = null
    ): void {
        const useThinInstances = renderingMesh.hasThinInstances;

        const scene = this.getScene();
        const engine = scene.getEngine();

        // Lights
        PrepareDefinesForLights(scene, mesh, defines, true, this._maxSimultaneousLights, this._disableLighting);
        defines._needNormals = true;

        // Multiview
        PrepareDefinesForMultiview(scene, defines);

        // PrePass
        const oit = this.needAlphaBlendingForMesh(mesh) && this.getScene().useOrderIndependentTransparency;
        PrepareDefinesForPrePass(scene, defines, this.canRenderToMRT && !oit);

        // Order independant transparency
        PrepareDefinesForOIT(scene, defines, oit);

        MaterialHelperGeometryRendering.PrepareDefines(engine.currentRenderPassId, mesh, defines);

        // Textures
        if (defines._areTexturesDirty) {
            defines._needUVs = false;
            for (let i = 1; i <= Constants.MAX_SUPPORTED_UV_SETS; ++i) {
                defines["MAINUV" + i] = false;
            }
            if (scene.texturesEnabled) {
                // Loop through samplers and prepare defines for each texture
                for (const key in this._samplersList) {
                    const sampler = this._samplersList[key];
                    if (sampler.value) {
                        PrepareDefinesForMergedUV(sampler.value, defines, sampler.textureDefine);
                        defines[sampler.textureDefine + "_GAMMA"] = sampler.value.gammaSpace;
                    } else {
                        defines[sampler.textureDefine] = false;
                    }
                }

                const radianceTexture = this._getRadianceTexture();
                const useSHInFragment: boolean =
                    this._forceIrradianceInFragment ||
                    this.realTimeFiltering ||
                    this._twoSidedLighting ||
                    engine.getCaps().maxVaryingVectors <= 8 ||
                    this._baseDiffuseRoughnessTexture != null;
                PrepareDefinesForIBL(scene, radianceTexture, defines, this.realTimeFiltering, this.realTimeFilteringQuality, !useSHInFragment);

                if (this._baseMetalnessTexture) {
                    defines.AOSTOREINMETALMAPRED = this._useAmbientOcclusionFromMetallicTextureRed;
                }

                defines.SPECULAR_WEIGHT_IN_ALPHA = this._useSpecularWeightFromAlpha;
                defines.SPECULAR_WEIGHT_FROM_SPECULAR_COLOR_TEXTURE = this._useSpecularWeightFromSpecularColorTexture;
                defines.SPECULAR_ROUGHNESS_ANISOTROPY_FROM_TANGENT_TEXTURE = this._useSpecularRoughnessAnisotropyFromTangentTexture;
                defines.COAT_ROUGHNESS_ANISOTROPY_FROM_TANGENT_TEXTURE = this._useCoatRoughnessAnisotropyFromTangentTexture;
                defines.COAT_ROUGHNESS_FROM_GREEN_CHANNEL = this._useCoatRoughnessFromGreenChannel;
                defines.SPECULAR_ROUGHNESS_FROM_METALNESS_TEXTURE_GREEN = this._useRoughnessFromMetallicTextureGreen;
                defines.FUZZ_ROUGHNESS_FROM_TEXTURE_ALPHA = this._useFuzzRoughnessFromTextureAlpha;
                defines.SUBSURFACE_WEIGHT_FROM_TEXTURE_ALPHA = this._useSubsurfaceWeightFromTextureAlpha;
                defines.BASE_METALNESS_FROM_METALNESS_TEXTURE_BLUE = this._useMetallicFromMetallicTextureBlue;
                defines.THIN_FILM_THICKNESS_FROM_THIN_FILM_TEXTURE = this._useThinFilmThicknessFromTextureGreen;
                defines.GEOMETRY_THICKNESS_FROM_GREEN_CHANNEL = this._useGeometryThicknessFromGreenChannel;

                if (this.geometryNormalTexture) {
                    if (this._useParallax && this.baseColorTexture && MaterialFlags.DiffuseTextureEnabled) {
                        defines.PARALLAX = true;
                        defines.PARALLAX_RHS = scene.useRightHandedSystem;
                        defines.PARALLAXOCCLUSION = !!this._useParallaxOcclusion;
                    } else {
                        defines.PARALLAX = false;
                    }
                    defines.OBJECTSPACE_NORMALMAP = this._useObjectSpaceNormalMap;
                } else {
                    defines.PARALLAX = false;
                    defines.PARALLAX_RHS = false;
                    defines.PARALLAXOCCLUSION = false;
                    defines.OBJECTSPACE_NORMALMAP = false;
                }

                if (this._environmentBRDFTexture && MaterialFlags.ReflectionTextureEnabled) {
                    defines.ENVIRONMENTBRDF = true;
                    defines.ENVIRONMENTBRDF_RGBD = this._environmentBRDFTexture.isRGBD;
                } else {
                    defines.ENVIRONMENTBRDF = false;
                    defines.ENVIRONMENTBRDF_RGBD = false;
                }

                if (this._environmentFuzzBRDFTexture) {
                    defines.FUZZENVIRONMENTBRDF = true;
                } else {
                    defines.FUZZENVIRONMENTBRDF = false;
                }

                if (this.hasTransparency) {
                    defines.REFRACTED_BACKGROUND = !!this._backgroundRefractionTexture && MaterialFlags.RefractionTextureEnabled;
                    defines.REFRACTION_HIGH_QUALITY_BLUR = this._refractionHighQualityBlur;
                    defines.REFRACTED_LIGHTS = true;
                    const radianceTexture = this._getRadianceTexture();
                    if (radianceTexture) {
                        defines.REFRACTED_ENVIRONMENT = MaterialFlags.RefractionTextureEnabled;
                        defines.REFRACTED_ENVIRONMENT_OPPOSITEZ = this.getScene().useRightHandedSystem ? !radianceTexture.invertZ : radianceTexture.invertZ;
                        defines.REFRACTED_ENVIRONMENT_LOCAL_CUBE = radianceTexture.isCube && (<any>radianceTexture).boundingBoxSize;
                    } else {
                        defines.REFRACTED_ENVIRONMENT = false;
                    }
                } else {
                    defines.REFRACTED_BACKGROUND = false;
                    defines.REFRACTED_LIGHTS = false;
                    defines.REFRACTED_ENVIRONMENT = false;
                }

                if (this._shouldUseAlphaFromBaseColorTexture()) {
                    defines.ALPHA_FROM_BASE_COLOR_TEXTURE = true;
                } else {
                    defines.ALPHA_FROM_BASE_COLOR_TEXTURE = false;
                }
            }

            if (this._lightFalloff === Material.LIGHTFALLOFF_STANDARD) {
                defines.USEPHYSICALLIGHTFALLOFF = false;
                defines.USEGLTFLIGHTFALLOFF = false;
            } else if (this._lightFalloff === Material.LIGHTFALLOFF_GLTF) {
                defines.USEPHYSICALLIGHTFALLOFF = false;
                defines.USEGLTFLIGHTFALLOFF = true;
            } else {
                defines.USEPHYSICALLIGHTFALLOFF = true;
                defines.USEGLTFLIGHTFALLOFF = false;
            }

            if (!this.backFaceCulling && this._twoSidedLighting) {
                defines.TWOSIDEDLIGHTING = true;
            } else {
                defines.TWOSIDEDLIGHTING = false;
            }

            // We need it to not invert normals in two sided lighting mode (based on the winding of the face)
            defines.MIRRORED = !!scene._mirroredCameraPosition;

            defines.SPECULARAA = engine.getCaps().standardDerivatives && this._enableSpecularAntiAliasing;
        }

        if (defines._areTexturesDirty || defines._areMiscDirty) {
            defines.ALPHATESTVALUE = `${this._alphaCutOff}${this._alphaCutOff % 1 === 0 ? "." : ""}`;
            defines.PREMULTIPLYALPHA = this.alphaMode === Constants.ALPHA_PREMULTIPLIED || this.alphaMode === Constants.ALPHA_PREMULTIPLIED_PORTERDUFF;
            defines.ALPHABLEND = this.needAlphaBlendingForMesh(mesh);
        }

        if (defines._areTexturesDirty) {
            defines.TEXTURE_REPETITION_MODE = engine.version > 1 || engine.isWebGPU ? this.textureRepetitionMode : 0;
        }

        if (defines._areImageProcessingDirty && this._imageProcessingConfiguration) {
            this._imageProcessingConfiguration.prepareDefines(defines);
        }

        defines.FORCENORMALFORWARD = this._forceNormalForward;

        defines.RADIANCEOCCLUSION = this._useRadianceOcclusion;

        defines.HORIZONOCCLUSION = this._useHorizonOcclusion;

        if (
            (this.specularRoughnessAnisotropy > 0.0 || this.coatRoughnessAnisotropy > 0.0) &&
            OpenPBRMaterial._noiseTextures[scene.uniqueId] &&
            MaterialFlags.ReflectionTextureEnabled
        ) {
            // ANISOTROPIC is used to include common shader functions needed for anisotropy
            // ANISOTROPIC_BASE is used to process anisotropy for the base layer
            // ANISOTROPIC_COAT is used to process anisotropy for the coat layer
            defines.ANISOTROPIC = true;
            if (!mesh.isVerticesDataPresent(VertexBuffer.TangentKind)) {
                defines._needUVs = true;
                defines.MAINUV1 = true;
            }
            if (this._useGltfStyleAnisotropy) {
                defines.USE_GLTF_STYLE_ANISOTROPY = true;
            }
            defines.ANISOTROPIC_BASE = this.specularRoughnessAnisotropy > 0.0;
            defines.ANISOTROPIC_COAT = this.coatRoughnessAnisotropy > 0.0;
        } else {
            defines.ANISOTROPIC = false;
            defines.USE_GLTF_STYLE_ANISOTROPY = false;
            defines.ANISOTROPIC_BASE = false;
            defines.ANISOTROPIC_COAT = false;
        }

        defines.THIN_FILM = this.thinFilmWeight > 0.0;
        defines.IRIDESCENCE = this.thinFilmWeight > 0.0;
        defines.DISPERSION = this.transmissionDispersionScale > 0.0;
        defines.SCATTERING = this.hasScattering;
        const _sssSampleCounts = [8, 16, 32];
        defines.SSS_SAMPLE_COUNT = _sssSampleCounts[this._sssQuality] ?? 16;
        defines.TRANSMISSION_SLAB = this.transmissionWeight > 0;
        defines.TRANSMISSION_SLAB_VOLUME = this.transmissionWeight > 0 && this.transmissionDepth > 0;
        defines.SUBSURFACE_SLAB = this.subsurfaceWeight > 0;
        // Determine whether we should use the prepass irradiance texture for scattering.
        // If this IS a prepass, we don't want to use the irradiance texture as it won't be available yet.
        if (!defines.PREPASS && (defines.SUBSURFACE_SLAB || defines.TRANSMISSION_SLAB_VOLUME)) {
            let usingGBuffer = false;
            if (!this.sssIrradianceTexture && scene.geometryBufferRenderer) {
                const irradianceTextureIndex = scene.geometryBufferRenderer.getTextureIndex(GeometryBufferRenderer.IRRADIANCE_TEXTURE_TYPE);
                this.sssIrradianceTexture = scene.geometryBufferRenderer.getGBuffer().textures[irradianceTextureIndex];
                usingGBuffer = true;
            }

            if (!this.sssDepthTexture && scene.geometryBufferRenderer) {
                const depthIndex = scene.geometryBufferRenderer.getTextureIndex(GeometryBufferRenderer.SCREENSPACE_DEPTH_TEXTURE_TYPE);
                this.sssDepthTexture = scene.geometryBufferRenderer.getGBuffer().textures[depthIndex];
                usingGBuffer = true;
            }
            if (this.sssIrradianceTexture && this.sssDepthTexture) {
                defines.USE_IRRADIANCE_TEXTURE_FOR_SCATTERING = true;
                if (usingGBuffer) {
                    defines.USE_IRRADIANCE_TEXTURE_FOR_SCATTERING_GBUFFER = true;
                }
            }
        }
        defines.FUZZ = this.fuzzWeight > 0 && MaterialFlags.ReflectionTextureEnabled;
        defines.GEOMETRY_THIN_WALLED = this.geometryThinWalled != 0;

        if (defines.FUZZ) {
            if (!mesh.isVerticesDataPresent(VertexBuffer.TangentKind)) {
                defines._needUVs = true;
                defines.MAINUV1 = true;
            }
            this._environmentFuzzBRDFTexture = GetEnvironmentFuzzBRDFTexture(this.getScene());
            defines.FUZZ_IBL_SAMPLES = this.fuzzSampleNumber;
        } else {
            this._environmentFuzzBRDFTexture = null;
            defines.FUZZENVIRONMENTBRDF = false;
            defines.FUZZ_IBL_SAMPLES = 0;
        }

        // Misc.
        if (defines._areMiscDirty) {
            PrepareDefinesForMisc(
                mesh,
                scene,
                this._useLogarithmicDepth,
                this.pointsCloud,
                this.fogEnabled,
                this.needAlphaTestingForMesh(mesh),
                defines,
                this._applyDecalMapAfterDetailMap,
                this._useVertexPulling,
                renderingMesh,
                this._isVertexOutputInvariant
            );
            defines.UNLIT = this._unlit || ((this.pointsCloud || this.wireframe) && !mesh.isVerticesDataPresent(VertexBuffer.NormalKind));
            defines.DEBUGMODE = this._debugMode;
        }

        // Values that need to be evaluated on every frame
        PrepareDefinesForFrameBoundValues(scene, engine, this, defines, useInstances ? true : false, useClipPlane, useThinInstances);

        // External config
        this._eventInfo.defines = defines;
        this._eventInfo.mesh = mesh;
        this._callbackPluginEventPrepareDefinesBeforeAttributes(this._eventInfo);

        // Attribs
        PrepareDefinesForAttributes(mesh, defines, true, true, true, this._transparencyMode !== Material.MATERIAL_OPAQUE);

        // External config
        this._callbackPluginEventPrepareDefines(this._eventInfo);
    }
}

let _Registered = false;
/**
 * Register side effects for openpbrMaterial.
 * Safe to call multiple times; only the first call has an effect.
 */
export function RegisterOpenpbrMaterial(): void {
    if (_Registered) {
        return;
    }
    _Registered = true;

    // OpenPBRMaterial serializes its image processing configuration, so the parser must be registered for clone/parse to work.
    RegisterImageProcessingConfiguration();

    RegisterClass("BABYLON.OpenPBRMaterial", OpenPBRMaterial);
}
